import { db } from '../firebase';
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs,
  updateDoc,
  deleteDoc,
  query, 
  where, 
  orderBy, 
  limit,
  Timestamp,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';

// ============================================
// WORKOUT TEMPLATE (Firestore only)
// ============================================

/**
 * @param {string} templateId — workout template id (e.g. multi_sport_m1_day_a)
 * @returns {Promise<object|null>} template with id, name, blocks, etc.
 */
export async function getWorkoutTemplate(templateId) {
  if (!templateId) return null;
  const snap = await getDoc(doc(db, 'workoutTemplates', templateId));
  if (snap.exists()) return { id: snap.id, ...snap.data() };
  return null;
}

/**
 * Get a warmup template by id from Firestore (DB is source of truth).
 * @param {string} templateId — warmup template id (e.g. multi_sport_warmup)
 * @returns {Promise<object|null>} template with id, name, exercises, etc.
 */
export async function getWarmupTemplate(templateId) {
  if (!templateId) return null;
  const snap = await getDoc(doc(db, 'warmup_templates', templateId));
  if (snap.exists()) return { id: snap.id, ...snap.data() };
  return null;
}

/**
 * Get a finisher template by id from Firestore (DB is source of truth).
 * @param {string} templateId — finisher template id (e.g. core_finisher_isometric)
 * @returns {Promise<object|null>} template with id, name, exercises, etc.
 */
export async function getFinisherTemplate(templateId) {
  if (!templateId) return null;
  const snap = await getDoc(doc(db, 'finisher_templates', templateId));
  if (snap.exists()) return { id: snap.id, ...snap.data() };
  return null;
}

/**
 * Get all workout templates from Firestore.
 * @param {string} [program] — optional program filter (e.g. 'volleyball', 'multi_sport_sprint', 'shared')
 * @returns {Promise<Array<object>>}
 */
export async function getWorkoutTemplates(program) {
  const snap = await getDocs(collection(db, 'workoutTemplates'));
  const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  if (!program) return all;
  return all.filter((t) => t.program === program || t.program === 'shared');
}

/**
 * Get all drill templates from Firestore.
 * @param {string} [program] — optional program filter
 * @returns {Promise<Array<object>>}
 */
export async function getDrillTemplates(program) {
  const snap = await getDocs(collection(db, 'drill_templates'));
  const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  if (!program) return all;
  return all.filter((t) => t.program === program || t.program === 'shared');
}

/**
 * Get all warmup templates from Firestore.
 * @param {string} [program] — optional program filter
 * @returns {Promise<Array<object>>}
 */
export async function getWarmupTemplates(program) {
  const snap = await getDocs(collection(db, 'warmup_templates'));
  const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  if (!program) return all;
  return all.filter((t) => t.program === program || t.program === 'shared');
}

/**
 * Get all finisher templates from Firestore.
 * @param {string} [program] — optional program filter
 * @returns {Promise<Array<object>>}
 */
export async function getFinisherTemplates(program) {
  const snap = await getDocs(collection(db, 'finisher_templates'));
  const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  if (!program) return all;
  return all.filter((t) => t.program === program || t.program === 'shared');
}

/**
 * Get all equipment from Firestore.
 * @returns {Promise<Array<object>>}
 */
export async function getEquipmentCatalog() {
  const snap = await getDocs(collection(db, 'equipment'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Get all equipment tiers from Firestore.
 * @returns {Promise<Array<object>>}
 */
export async function getEquipmentTiers() {
  const snap = await getDocs(collection(db, 'equipment_tiers'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ============================================
// USER PROFILE FUNCTIONS
// ============================================

/**
 * Create a new user profile
 */
export async function createUserProfile(userId, profileData) {
  try {
  const userRef = doc(db, 'users', userId);
    await setDoc(userRef, {
      uid: userId,
      ...profileData,
    createdAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
      onboardingCompleted: false,
    }, { merge: true });
    console.log('[createUserProfile] Created profile for user:', userId);
    return { success: true };
  } catch (error) {
    console.error('[createUserProfile] Error:', error);
    throw error;
  }
}

/**
 * Get user profile
 */
export async function getUserProfile(userId) {
  try {
  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);
  if (userSnap.exists()) {
    return userSnap.data();
  }
  return null;
  } catch (error) {
    console.error('[getUserProfile] Error:', error);
    throw error;
  }
}

/**
 * Update user profile
 */
export async function updateUserProfile(userId, updates) {
  try {
  const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
    console.log('[updateUserProfile] Updated profile for user:', userId);
    return { success: true };
  } catch (error) {
    console.error('[updateUserProfile] Error:', error);
    throw error;
  }
}

// ============================================
// PROGRAM FUNCTIONS
// ============================================

/**
 * Get current program for user
 */
export async function getCurrentProgram(userId) {
  try {
    const userProfile = await getUserProfile(userId);
    if (!userProfile?.currentProgramId) {
      return null;
    }

    const programRef = doc(db, 'users', userId, 'programs', userProfile.currentProgramId);
    const programSnap = await getDoc(programRef);
    
    if (!programSnap.exists()) {
      return null;
    }

    const programData = programSnap.data();

    // Fetch ALL macrocycles and mesocycles for this user, then resolve active ones by today's date
    const macrosRef = collection(db, 'users', userId, 'macrocycles');
    const mesosRef = collection(db, 'users', userId, 'mesocycles');
    const [macrosSnap, mesosSnap] = await Promise.all([getDocs(macrosRef), getDocs(mesosRef)]);
    const allMacros = macrosSnap.docs.map((d) => d.data());
    const allMesos = mesosSnap.docs.map((d) => d.data());

    const today = new Date();
    const macrocycle = findActiveMacrocycle(allMacros, today) || null;
    let mesocycle = null;
    if (macrocycle) {
      mesocycle = findActiveMesocycle(allMesos, macrocycle, today) || null;
    }

    // Compute current week within the active mesocycle
    let currentWeek = 1;
    if (mesocycle?.startDate) {
      const mesoStart = new Date(mesocycle.startDate + 'T00:00:00');
      const diffMs = today - mesoStart;
      if (diffMs >= 0) {
        currentWeek = Math.floor(diffMs / (7 * 24 * 60 * 60 * 1000)) + 1;
      }
    }

    return {
      program: programData,
      macrocycle,
      mesocycle,
      currentWeek,
      allMacrocycles: allMacros,
      allMesocycles: allMesos,
    };
  } catch (error) {
    console.error('[getCurrentProgram] Error:', error);
    throw error;
  }
}

// ============================================
// SCHEDULED ACTIVITIES FUNCTIONS
// ============================================

/**
 * Get scheduled activities for a specific date
 */
export async function getScheduledActivitiesForDate(userId, dateStr) {
  try {
    // Get scheduled activities
    const activitiesRef = collection(db, 'users', userId, 'scheduledActivities');
    let q;
    try {
      q = query(
        activitiesRef,
        where('scheduledDate', '==', dateStr),
        orderBy('scheduledTime', 'asc')
      );
    } catch (error) {
      // If orderBy fails, try without it
      q = query(activitiesRef, where('scheduledDate', '==', dateStr));
    }

    // Fetch scheduled activities and the day's activityLog in parallel.
    // The log read does not depend on the activities query, so running them
    // concurrently avoids two serialized network round-trips per day.
    const logRef = doc(db, 'users', userId, 'activityLog', dateStr);
    const [snapshot, logSnap] = await Promise.all([
      getDocs(q),
      getDoc(logRef).catch(() => null), // completion status is best-effort
    ]);

    let scheduledActivities = snapshot.docs.map(doc => {
      const data = doc.data();
      // Convert time to 12-hour format if it's in 24-hour format
      const rawTime = data.scheduledTime;
      let displayTime = rawTime || null;
      if (rawTime && typeof rawTime === 'string') {
        displayTime = (rawTime.includes('AM') || rawTime.includes('PM'))
          ? rawTime
          : convertTo12Hour(rawTime);
      }
      
      return {
        activityId: doc.id,
        ...data,
        scheduledTime: displayTime,
      };
    });
    
    // Get activityLog to check completion status
    let completedActivityIds = new Set();
    if (logSnap && logSnap.exists()) {
      const logData = logSnap.data();
      const completedActivities = logData.completedActivities || [];
      completedActivityIds = new Set(completedActivities.map(a => a.activityId));
    }
    
    // Mark scheduled activities as completed if they're in activityLog
    scheduledActivities = scheduledActivities.map(activity => {
      if (completedActivityIds.has(activity.activityId)) {
        return {
          ...activity,
          status: 'completed'
        };
      }
      return activity;
    });

    // Note: We only show activities from scheduledActivities
    // Orphaned activities in activityLog (without corresponding scheduledActivity) are ignored
    // The activityLog is only used to mark scheduledActivities as completed (done above)
    
    // Sort by scheduledTime if available (activities with time first), then by name
    return scheduledActivities.sort((a, b) => {
      const timeA = a.scheduledTime || '';
      const timeB = b.scheduledTime || '';
      if (timeA && !timeB) return -1;
      if (!timeA && timeB) return 1;
      if (timeA && timeB && timeA !== timeB) {
        return timeA.localeCompare(timeB);
      }
      return (a.name || '').localeCompare(b.name || '');
    });
  } catch (error) {
    console.error('[getScheduledActivitiesForDate] Error:', error);
    return [];
  }
}

/**
 * Get scheduled activities for a month
 */
export async function getScheduledActivitiesForMonth(userId, year, month) {
  try {
    const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const endDate = `${year}-${String(month + 1).padStart(2, '0')}-31`;

    const activitiesRef = collection(db, 'users', userId, 'scheduledActivities');
    const q = query(
      activitiesRef,
      where('scheduledDate', '>=', startDate),
      where('scheduledDate', '<=', endDate),
      orderBy('scheduledDate', 'asc')
    );

    const snapshot = await getDocs(q);
    const activities = snapshot.docs.map(doc => ({
      activityId: doc.id,
      ...doc.data(),
    }));

    return activities;
  } catch (error) {
    console.error('[getScheduledActivitiesForMonth] Error:', error);
    return [];
  }
}

/**
 * Get scheduled activities for a set of days (e.g. the visible week) in a single
 * ranged query, cross-referencing each day's activityLog for completion status.
 *
 * This is the batched equivalent of calling getScheduledActivitiesForDate once
 * per day: it issues ONE ranged query plus the per-day activityLog reads in
 * parallel, then buckets/sorts the results locally.
 *
 * @param {string} userId
 * @param {string[]} dateStrs - list of 'YYYY-MM-DD' dates to include
 * @returns {Promise<Object<string, Array>>} map of dateStr -> sorted Activity[]
 *          (every requested date is present, with [] when it has no activities)
 */
export async function getScheduledActivitiesForWeek(userId, dateStrs) {
  const result = {};
  if (!userId || !Array.isArray(dateStrs) || dateStrs.length === 0) return result;

  // Ensure every requested day is represented, even if it has no activities.
  dateStrs.forEach((d) => { result[d] = []; });

  const sorted = [...dateStrs].sort();
  const startDate = sorted[0];
  const endDate = sorted[sorted.length - 1];
  const wanted = new Set(dateStrs);

  try {
    const activitiesRef = collection(db, 'users', userId, 'scheduledActivities');
    // Range + orderBy on the same field (scheduledDate) needs no composite index.
    // Per-day time ordering is handled by the local sort below.
    const q = query(
      activitiesRef,
      where('scheduledDate', '>=', startDate),
      where('scheduledDate', '<=', endDate),
      orderBy('scheduledDate', 'asc')
    );

    // Fetch the ranged activities query and each day's activityLog concurrently.
    const logRefs = dateStrs.map((d) => doc(db, 'users', userId, 'activityLog', d));
    const [snapshot, ...logSnaps] = await Promise.all([
      getDocs(q),
      ...logRefs.map((ref) => getDoc(ref).catch(() => null)), // completion is best-effort
    ]);

    // Build the set of completed activityIds per date.
    const completedByDate = {};
    logSnaps.forEach((logSnap, i) => {
      const set = new Set();
      if (logSnap && logSnap.exists()) {
        (logSnap.data().completedActivities || []).forEach((a) => set.add(a.activityId));
      }
      completedByDate[dateStrs[i]] = set;
    });

    snapshot.docs.forEach((docSnap) => {
      const data = docSnap.data();
      const d = data.scheduledDate;
      if (!wanted.has(d)) return; // guard against non-contiguous ranges

      const rawTime = data.scheduledTime;
      let displayTime = rawTime || null;
      if (rawTime && typeof rawTime === 'string') {
        displayTime = (rawTime.includes('AM') || rawTime.includes('PM'))
          ? rawTime
          : convertTo12Hour(rawTime);
      }

      const activity = { activityId: docSnap.id, ...data, scheduledTime: displayTime };
      if (completedByDate[d] && completedByDate[d].has(docSnap.id)) {
        activity.status = 'completed';
      }
      result[d].push(activity);
    });

    // Sort each day identically to getScheduledActivitiesForDate.
    Object.keys(result).forEach((d) => {
      result[d].sort((a, b) => {
        const timeA = a.scheduledTime || '';
        const timeB = b.scheduledTime || '';
        if (timeA && !timeB) return -1;
        if (!timeA && timeB) return 1;
        if (timeA && timeB && timeA !== timeB) return timeA.localeCompare(timeB);
        return (a.name || '').localeCompare(b.name || '');
      });
    });

    return result;
  } catch (error) {
    console.error('[getScheduledActivitiesForWeek] Error:', error);
    return result;
  }
}

/**
 * Get today's strength workout or the next scheduled strength workout
 * @param {string} userId - User ID
 * @returns {Promise<{workout: object, date: string, isToday: boolean} | null>}
 */
export async function getNextStrengthWorkout(userId) {
  try {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    
    // First check today's activities for a strength workout
    const todayActivities = await getScheduledActivitiesForDate(userId, todayStr);
    const todayStrengthWorkout = todayActivities.find(a => 
      a.activityType === 'strength_workout' || a.activityType === 'strength'
    );
    
    if (todayStrengthWorkout) {
      console.log('[getNextStrengthWorkout] Found today\'s strength workout:', todayStrengthWorkout.name);
      return {
        workout: todayStrengthWorkout,
        date: todayStr,
        isToday: true
      };
    }
    
    // No strength workout today - look for the next one in the next 30 days
    const activitiesRef = collection(db, 'users', userId, 'scheduledActivities');
    
    // Get next 30 days of activities
    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() + 30);
    const endDateStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
    
    const q = query(
      activitiesRef,
      where('scheduledDate', '>', todayStr),
      where('scheduledDate', '<=', endDateStr),
      orderBy('scheduledDate', 'asc')
    );
    
    const snapshot = await getDocs(q);
    const futureActivities = snapshot.docs.map(doc => ({
      activityId: doc.id,
      ...doc.data()
    }));
    
    // Find the first strength workout
    const nextStrengthWorkout = futureActivities.find(a => 
      a.activityType === 'strength_workout' || a.activityType === 'strength'
    );
    
    if (nextStrengthWorkout) {
      console.log('[getNextStrengthWorkout] Found next strength workout:', nextStrengthWorkout.name, 'on', nextStrengthWorkout.scheduledDate);
      return {
        workout: nextStrengthWorkout,
        date: nextStrengthWorkout.scheduledDate,
        isToday: false
      };
    }
    
    console.log('[getNextStrengthWorkout] No strength workouts found in next 30 days');
    return null;
  } catch (error) {
    console.error('[getNextStrengthWorkout] Error:', error);
    return null;
  }
}

/**
 * Update activities for a date (add, update, delete)
 */
export async function updateActivitiesForDate(userId, dateStr, activityNames, existingActivities = []) {
  try {
    const batch = writeBatch(db);
    let added = 0;
    let updated = 0;
    let deleted = 0;

    // Get existing activities for this date
    const existingActivitiesMap = new Map();
    for (const activity of existingActivities) {
      if (activity.activityId) {
        existingActivitiesMap.set(activity.name, activity);
      }
    }

    // Find activities to delete (in existing but not in new list)
    const newNamesSet = new Set(activityNames.map(a => typeof a === 'string' ? a : a.name));
    for (const [name, activity] of existingActivitiesMap.entries()) {
      if (!newNamesSet.has(name)) {
        const activityRef = doc(db, 'users', userId, 'scheduledActivities', activity.activityId);
        batch.delete(activityRef);
        deleted++;
      }
    }

    // Add or update activities
    for (const activityName of activityNames) {
      const name = typeof activityName === 'string' ? activityName : activityName.name;
      const duration = typeof activityName === 'string' ? 45 : (activityName.duration || 45);

      const existing = existingActivitiesMap.get(name);
      if (existing && existing.activityId) {
        // Update existing
        const activityRef = doc(db, 'users', userId, 'scheduledActivities', existing.activityId);
        batch.update(activityRef, {
          estimatedDuration: duration,
          updatedAt: serverTimestamp(),
        });
        updated++;
      } else {
        // Create new
        const newActivityRef = doc(collection(db, 'users', userId, 'scheduledActivities'));
        batch.set(newActivityRef, {
          scheduledDate: dateStr,
          scheduledTime: null,
          activityType: 'custom',
          source: 'user',
          isEdited: false,
          name: name,
          estimatedDuration: duration,
          status: 'scheduled',
          completedAt: null,
          completionSource: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        added++;
      }
    }

    await batch.commit();
    console.log(`[updateActivitiesForDate] Updated: added=${added}, updated=${updated}, deleted=${deleted}`);
    return { added, updated, deleted };
  } catch (error) {
    console.error('[updateActivitiesForDate] Error:', error);
    throw error;
  }
}

/**
 * Add a single activity with full details (duration, distance, time)
 * @param {string} userId - User ID
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @param {object} activityData - Activity details
 * @param {string} activityData.name - Activity name
 * @param {number|null} activityData.duration - Duration in minutes
 * @param {number|null} activityData.distance - Distance value
 * @param {string|null} activityData.distanceUnit - 'miles' or 'km'
 * @param {string|null} activityData.scheduledTime - Time in HH:mm format
 */
export async function addSingleActivity(userId, dateStr, activityData) {
  try {
    const { name, duration, durationSeconds, distance, distanceUnit, scheduledTime, laps, poolLength } = activityData;
    
    const newActivityRef = doc(collection(db, 'users', userId, 'scheduledActivities'));
    await setDoc(newActivityRef, {
      scheduledDate: dateStr,
      scheduledTime: scheduledTime || null,
      activityType: 'custom',
      source: 'user',
      isEdited: false,
      name: name,
      estimatedDuration: duration || null,
      estimatedDurationSeconds: durationSeconds || null,
      estimatedDistance: distance || null,
      distanceUnit: distanceUnit || null,
      // Swimming-specific fields
      laps: laps || null,
      poolLength: poolLength || null,
      status: 'scheduled',
      completedAt: null,
      completionSource: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    console.log(`[addSingleActivity] Added activity: ${name} on ${dateStr}`);
    return { success: true, activityId: newActivityRef.id };
  } catch (error) {
    console.error('[addSingleActivity] Error:', error);
    throw error;
  }
}

/**
 * Update an existing activity with new details
 * @param {string} userId - User ID
 * @param {string} activityId - Activity document ID
 * @param {object} updates - Fields to update
 */
export async function updateActivityDetails(userId, activityId, updates) {
  try {
    const activityRef = doc(db, 'users', userId, 'scheduledActivities', activityId);
    
    // Build update object, only including non-undefined values
    const updateData = {
      updatedAt: serverTimestamp(),
    };
    
    if (updates.estimatedDuration !== undefined) {
      updateData.estimatedDuration = updates.estimatedDuration;
    }
    if (updates.estimatedDurationSeconds !== undefined) {
      updateData.estimatedDurationSeconds = updates.estimatedDurationSeconds;
    }
    if (updates.estimatedDistance !== undefined) {
      updateData.estimatedDistance = updates.estimatedDistance;
    }
    if (updates.distanceUnit !== undefined) {
      updateData.distanceUnit = updates.distanceUnit;
    }
    if (updates.scheduledTime !== undefined) {
      updateData.scheduledTime = updates.scheduledTime;
    }
    // Swimming-specific fields
    if (updates.laps !== undefined) {
      updateData.laps = updates.laps;
    }
    if (updates.poolLength !== undefined) {
      updateData.poolLength = updates.poolLength;
    }
    
    await updateDoc(activityRef, updateData);
    
    console.log(`[updateActivityDetails] Updated activity: ${activityId}`);
    return { success: true };
  } catch (error) {
    console.error('[updateActivityDetails] Error:', error);
    throw error;
  }
}

/**
 * Update scheduled time for all strength workouts
 * @param {string} userId - User ID
 * @param {string} newTime - New scheduled time in HH:mm format (e.g., "06:30")
 * @returns {object} - { success: boolean, updatedCount: number }
 */
export async function updateAllStrengthWorkoutTimes(userId, newTime) {
  try {
    if (!userId || !newTime) {
      console.error('[updateAllStrengthWorkoutTimes] Missing required parameters:', { userId, newTime });
      throw new Error('Missing required parameters: userId or newTime');
    }
    
    const activitiesRef = collection(db, 'users', userId, 'scheduledActivities');
    const snapshot = await getDocs(activitiesRef);
    
    // Filter for strength workouts only
    const strengthWorkouts = snapshot.docs.filter(doc => {
      const data = doc.data();
      return data.activityType === 'strength_workout' || data.activityType === 'strength';
    });
    
    if (strengthWorkouts.length === 0) {
      console.log('[updateAllStrengthWorkoutTimes] No strength workouts found');
      return { success: true, updatedCount: 0 };
    }
    
    // Update all strength workouts in batches (Firestore limit: 500 per batch)
    const batchSize = 500;
    let updatedCount = 0;
    
    for (let i = 0; i < strengthWorkouts.length; i += batchSize) {
      const batch = writeBatch(db);
      const batchDocs = strengthWorkouts.slice(i, i + batchSize);
      
      batchDocs.forEach(docSnap => {
        batch.update(docSnap.ref, {
          scheduledTime: newTime,
          updatedAt: serverTimestamp(),
        });
      });
      
      await batch.commit();
      updatedCount += batchDocs.length;
    }
    
    console.log(`[updateAllStrengthWorkoutTimes] Updated ${updatedCount} strength workouts to time: ${newTime}`);
    return { success: true, updatedCount };
  } catch (error) {
    console.error('[updateAllStrengthWorkoutTimes] Error:', error);
    throw error;
  }
}

/**
 * Delete a scheduled activity
 * @param {string} userId - User ID
 * @param {string} activityId - Activity document ID
 */
export async function deleteActivity(userId, activityId) {
  try {
    if (!userId || !activityId) {
      console.error('[deleteActivity] Missing required parameters:', { userId, activityId });
      throw new Error('Missing required parameters: userId or activityId');
    }
    
    const activityRef = doc(db, 'users', userId, 'scheduledActivities', activityId);
    await deleteDoc(activityRef);
    
    console.log(`[deleteActivity] Deleted activity: ${activityId}`);
    return { success: true };
  } catch (error) {
    console.error('[deleteActivity] Error:', error);
    throw error;
  }
}

/**
 * Mark activity as complete or incomplete
 */
export async function markActivityComplete(userId, activityId, dateStr, isComplete) {
  try {
    if (!userId || !activityId || !dateStr) {
      console.error('[markActivityComplete] Missing required parameters:', { userId, activityId, dateStr });
      throw new Error('Missing required parameters: userId, activityId, or dateStr');
    }
    const activityRef = doc(db, 'users', userId, 'scheduledActivities', activityId);
    const activitySnap = await getDoc(activityRef);
    const activityExists = activitySnap.exists();
    const activityData = activityExists ? activitySnap.data() : null;
    
    // Get estimated duration from activity or use default based on type
    const getDefaultDuration = (name) => {
      const nameLower = (name || '').toLowerCase();
      if (nameLower.includes('strength') || nameLower.includes('workout')) return 45;
      if (nameLower.includes('run') || nameLower.includes('trail')) return 30;
      if (nameLower.includes('bike') || nameLower.includes('cycling')) return 60;
      if (nameLower.includes('swim')) return 45;
      if (nameLower.includes('walk')) return 30;
      return 30; // Default 30 minutes
    };
    
    // Ensure we always have a name - try multiple sources
    let activityName = activityData?.name;
    
    // If name is missing, try to extract from activityId (last part after last dash)
    if (!activityName && activityId) {
      const parts = activityId.split('-');
      const lastPart = parts[parts.length - 1];
      // Capitalize first letter
      activityName = lastPart.charAt(0).toUpperCase() + lastPart.slice(1);
      console.warn(`[markActivityComplete] Activity name missing for ${activityId}, extracted from ID: ${activityName}`);
    }
    
    // Final fallback
    if (!activityName) {
      activityName = 'Activity';
      console.warn(`[markActivityComplete] Activity name missing for ${activityId}, using fallback: ${activityName}`);
    }
    
    // Get duration in minutes (for backward compatibility)
    const duration = activityData?.estimatedDuration || 
                     activityData?.duration || 
                     getDefaultDuration(activityName);
    
    // Get duration in seconds (preferred for precision)
    // Use estimatedDurationSeconds if available, otherwise convert from minutes
    const durationSeconds = activityData?.estimatedDurationSeconds || 
                            (duration ? duration * 60 : getDefaultDuration(activityName) * 60);
    
    if (isComplete) {
      // Only update scheduledActivity if it exists
      if (activityExists) {
        const updateData = {
          status: 'completed',
          completedAt: serverTimestamp(),
          completionSource: 'manual',
          estimatedDuration: duration,
          estimatedDurationSeconds: durationSeconds,
          updatedAt: serverTimestamp(),
        };
        
        // Only update name if it was missing
        if (!activityData?.name) {
          updateData.name = activityName;
        }
        
        await updateDoc(activityRef, updateData);
      }

      // Update activity log for this date
      const logRef = doc(db, 'users', userId, 'activityLog', dateStr);
      const logSnap = await getDoc(logRef);

      const completedActivity = {
        activityId,
        activityType: activityData?.activityType || 'custom',
        name: activityName, // Always include name
        duration: duration, // Minutes (backward compatibility)
        durationSeconds: durationSeconds, // Seconds (precision)
        // Distance data (for running, cycling, etc.)
        distance: activityData?.estimatedDistance || null,
        distanceUnit: activityData?.distanceUnit || null,
        // Swimming-specific data
        laps: activityData?.laps || null,
        poolLength: activityData?.poolLength || null,
        completedAt: Timestamp.now(),
        completionSource: 'manual',
      };

      // Queue for HealthKit export if user has HealthKit enabled
      try {
        const userProfile = await getUserProfile(userId);
        if (userProfile?.appleHealthConnected) {
          await queueActivityForHealthKitExport(userId, activityId, dateStr, completedActivity);
        }
      } catch (queueError) {
        // Don't fail the completion if queueing fails
        console.warn('[markActivityComplete] Failed to queue for HealthKit export:', queueError);
      }

      if (logSnap.exists()) {
        const logData = logSnap.data();
        const completedActivities = logData.completedActivities || [];
        const existingMetrics = logData.metrics || {};
        const activityExists = completedActivities.find(a => a.activityId === activityId);
        
        if (!activityExists) {
          completedActivities.push(completedActivity);

          // Update metrics - add duration to total (use seconds for precision)
          const newDurationSeconds = (existingMetrics.workoutDurationSeconds || (existingMetrics.workoutDuration || 0) * 60) + durationSeconds;
          const newDuration = Math.round(newDurationSeconds / 60); // Round to minutes for backward compat

          await updateDoc(logRef, {
            completedActivities,
            hasCompletedActivity: true,
            metrics: {
              ...existingMetrics,
              workoutDuration: newDuration,
              workoutDurationSeconds: newDurationSeconds,
            },
            updatedAt: serverTimestamp(),
          });
    }
  } else {
        await setDoc(logRef, {
          date: dateStr,
          completedActivities: [completedActivity],
          hasCompletedActivity: true,
          metrics: {
            lbsMoved: 0,
            workoutDuration: duration,
            workoutDurationSeconds: durationSeconds,
            setsCompleted: 0,
            repsCompleted: 0,
          },
          streakDay: 1,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
    } else {
      await updateDoc(activityRef, {
        status: 'scheduled',
        completedAt: null,
        completionSource: null,
        updatedAt: serverTimestamp(),
      });

      // Remove from activity log and update metrics
      const logRef = doc(db, 'users', userId, 'activityLog', dateStr);
      const logSnap = await getDoc(logRef);
      if (logSnap.exists()) {
        const logData = logSnap.data();
        const existingMetrics = logData.metrics || {};
        
        // Find the activity being removed to subtract its duration
        const removedActivity = (logData.completedActivities || []).find(
          a => a.activityId === activityId
        );
        const removedDuration = removedActivity?.duration || duration || 0;
        const removedDurationSeconds = removedActivity?.durationSeconds || (removedDuration * 60);
        
        const completedActivities = (logData.completedActivities || []).filter(
          a => a.activityId !== activityId
        );

        // Subtract duration from total
        const newDurationSeconds = Math.max(0, (existingMetrics.workoutDurationSeconds || (existingMetrics.workoutDuration || 0) * 60) - removedDurationSeconds);
        const newDuration = Math.round(newDurationSeconds / 60);

        await updateDoc(logRef, {
          completedActivities,
          hasCompletedActivity: completedActivities.length > 0,
          metrics: {
            ...existingMetrics,
            workoutDuration: newDuration,
            workoutDurationSeconds: newDurationSeconds,
          },
          updatedAt: serverTimestamp(),
        });
      }
    }

    return { success: true };
  } catch (error) {
    console.error('[markActivityComplete] Error:', error);
    throw error;
  }
}

// ============================================
// HEALTHKIT EXPORT QUEUE FUNCTIONS
// ============================================

/**
 * Queue an activity for HealthKit export
 * Called when an activity is completed manually in the PWA
 * iOS app will sync these when it opens
 */
export async function queueActivityForHealthKitExport(userId, activityId, dateStr, activityData) {
  try {
    if (!userId || !activityId || !dateStr) {
      console.error('[queueActivityForHealthKitExport] Missing required parameters:', { userId, activityId, dateStr });
      throw new Error('Missing required parameters: userId, activityId, or dateStr');
    }

    const queueRef = doc(db, 'users', userId, 'healthKitExportQueue', activityId);
    
    // Prepare export data
    const exportData = {
      activityId,
      date: dateStr,
      activityType: activityData.activityType || 'custom',
      name: activityData.name || 'Activity',
      durationSeconds: activityData.durationSeconds || (activityData.duration ? activityData.duration * 60 : 0),
      distance: activityData.distance || null,
      distanceUnit: activityData.distanceUnit || null,
      // Swimming-specific
      laps: activityData.laps || null,
      poolLength: activityData.poolLength || null,
      // Status
      status: 'pending', // 'pending', 'synced', 'failed'
      queuedAt: serverTimestamp(),
      syncedAt: null,
      syncAttempts: 0,
      error: null,
    };

    await setDoc(queueRef, exportData, { merge: true });
    console.log(`[queueActivityForHealthKitExport] Queued activity ${activityId} for HealthKit export`);
    return { success: true, queueId: activityId };
  } catch (error) {
    console.error('[queueActivityForHealthKitExport] Error:', error);
    throw error;
  }
}

/**
 * Get pending HealthKit export queue items
 * Called by iOS app to sync queued activities to HealthKit
 */
export async function getPendingHealthKitExportQueue(userId, limit = 100) {
  try {
    if (!userId) {
      console.error('[getPendingHealthKitExportQueue] Missing userId');
      throw new Error('Missing required parameter: userId');
    }

    const queueRef = collection(db, 'users', userId, 'healthKitExportQueue');
    const q = query(
      queueRef,
      where('status', '==', 'pending'),
      orderBy('queuedAt', 'asc')
    );

    const snapshot = await getDocs(q);
    const pendingItems = [];
    
    snapshot.docs.forEach(docSnap => {
      if (pendingItems.length < limit) {
        pendingItems.push({
          id: docSnap.id,
          ...docSnap.data()
        });
      }
    });

    console.log(`[getPendingHealthKitExportQueue] Found ${pendingItems.length} pending items for user ${userId}`);
    return pendingItems;
  } catch (error) {
    console.error('[getPendingHealthKitExportQueue] Error:', error);
    throw error;
  }
}

/**
 * Mark a queued activity as synced to HealthKit
 * Called by iOS app after successfully exporting to HealthKit
 */
export async function markHealthKitExportSynced(userId, activityId, success = true, error = null) {
  try {
    if (!userId || !activityId) {
      console.error('[markHealthKitExportSynced] Missing required parameters:', { userId, activityId });
      throw new Error('Missing required parameters: userId or activityId');
    }

    const queueRef = doc(db, 'users', userId, 'healthKitExportQueue', activityId);
    const queueSnap = await getDoc(queueRef);

    if (!queueSnap.exists()) {
      console.warn(`[markHealthKitExportSynced] Queue item not found: ${activityId}`);
      return { success: false, error: 'Queue item not found' };
    }

    const queueData = queueSnap.data();
    const updateData = {
      status: success ? 'synced' : 'failed',
      syncedAt: success ? serverTimestamp() : null,
      syncAttempts: (queueData.syncAttempts || 0) + 1,
      error: error || null,
      updatedAt: serverTimestamp(),
    };

    await updateDoc(queueRef, updateData);
    console.log(`[markHealthKitExportSynced] Marked ${activityId} as ${success ? 'synced' : 'failed'}`);
    return { success: true };
  } catch (error) {
    console.error('[markHealthKitExportSynced] Error:', error);
    throw error;
  }
}

/**
 * Get HealthKit export queue history
 * Shows all queued, synced, and failed exports
 */
export async function getHealthKitExportQueueHistory(userId, limit = 50) {
  try {
    if (!userId) {
      console.error('[getHealthKitExportQueueHistory] Missing userId');
      throw new Error('Missing required parameter: userId');
    }

    const queueRef = collection(db, 'users', userId, 'healthKitExportQueue');
    const q = query(
      queueRef,
      orderBy('queuedAt', 'desc')
    );

    const snapshot = await getDocs(q);
    const history = [];
    
    snapshot.docs.forEach(docSnap => {
      if (history.length < limit) {
        history.push({
          id: docSnap.id,
          ...docSnap.data()
        });
      }
    });

    return history;
  } catch (error) {
    console.error('[getHealthKitExportQueueHistory] Error:', error);
    throw error;
  }
}

/**
 * Clear old synced items from the queue (optional cleanup)
 * Keeps failed items for retry, removes old synced items
 */
export async function clearOldHealthKitExportQueue(userId, daysToKeep = 30) {
  try {
    if (!userId) {
      console.error('[clearOldHealthKitExportQueue] Missing userId');
      throw new Error('Missing required parameter: userId');
    }

    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);
    const cutoffTimestamp = Timestamp.fromDate(cutoffDate);

    const queueRef = collection(db, 'users', userId, 'healthKitExportQueue');
    const q = query(
      queueRef,
      where('status', '==', 'synced'),
      where('syncedAt', '<', cutoffTimestamp)
    );

    const snapshot = await getDocs(q);
    const batch = writeBatch(db);
    let deletedCount = 0;

    snapshot.docs.forEach(docSnap => {
      batch.delete(docSnap.ref);
      deletedCount++;
    });

    await batch.commit();
    console.log(`[clearOldHealthKitExportQueue] Cleared ${deletedCount} old synced items`);
    return { success: true, deletedCount };
  } catch (error) {
    console.error('[clearOldHealthKitExportQueue] Error:', error);
    throw error;
  }
}

/**
 * Get completed activity days for a month
 * @returns {Promise<number[]>} Array of day numbers (1-31) that have completed activities
 */
export async function getCompletedActivityDays(userId, year, month) {
  try {
    const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const endDate = `${year}-${String(month + 1).padStart(2, '0')}-31`;

    const logRef = collection(db, 'users', userId, 'activityLog');
    
    // Query without orderBy to avoid requiring composite index
    // Since we only need the dates, we can sort client-side
    const q = query(
      logRef,
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      where('hasCompletedActivity', '==', true)
    );

    const snapshot = await getDocs(q);
    const days = snapshot.docs.map(doc => {
      const data = doc.data();
      // Extract day number from YYYY-MM-DD format
      const dayNumber = parseInt(data.date.split('-')[2], 10);
      return dayNumber;
    });

    return days;
  } catch (error) {
    console.error('[getCompletedActivityDays] Error:', error);
    return [];
  }
}

/**
 * Calculate current streak (consecutive days with completed activities)
 * @param {string} userId - User ID
 * @returns {Promise<number>} Current streak in days
 */
export async function getCurrentStreak(userId) {
  try {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    
    // Get activity logs going back up to 365 days to find the streak
    const startDate = new Date(today);
    startDate.setDate(startDate.getDate() - 365);
    const startDateStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;

    const logRef = collection(db, 'users', userId, 'activityLog');
    const q = query(
      logRef,
      where('date', '>=', startDateStr),
      where('date', '<=', todayStr),
      where('hasCompletedActivity', '==', true),
      orderBy('date', 'desc')
    );

    const snapshot = await getDocs(q);
    const completedDates = snapshot.docs.map(doc => doc.data().date).sort().reverse();
    
    if (completedDates.length === 0) {
      return 0;
    }

    // Calculate streak by checking consecutive days from today backwards
    let streak = 0;
    const checkDate = new Date(today);
    
    // Check if today has activity
    if (completedDates.includes(todayStr)) {
      streak = 1;
      checkDate.setDate(checkDate.getDate() - 1);
    } else {
      // If today doesn't have activity, start from yesterday
      checkDate.setDate(checkDate.getDate() - 1);
    }

    // Count consecutive days going backwards
    while (checkDate >= startDate) {
      const dateStr = `${checkDate.getFullYear()}-${String(checkDate.getMonth() + 1).padStart(2, '0')}-${String(checkDate.getDate()).padStart(2, '0')}`;
      
      if (completedDates.includes(dateStr)) {
        streak++;
        checkDate.setDate(checkDate.getDate() - 1);
      } else {
        break;
      }
    }

    return streak;
  } catch (error) {
    console.error('[getCurrentStreak] Error:', error);
    return 0;
  }
}

/**
 * Get streak data for charts (12 data points showing streak values)
 * @param {string} userId - User ID
 * @param {number} year - Year - used if startDateStr/endDateStr not provided
 * @param {number} month - Month (0-11) - used if startDateStr/endDateStr not provided
 * @param {string} startDateStr - Optional start date in YYYY-MM-DD format
 * @param {string} endDateStr - Optional end date in YYYY-MM-DD format
 * @returns {Promise<Array<{value: number}>>} Array of 12 streak values
 */
export async function getMonthlyStreakData(userId, year, month, startDateStr = null, endDateStr = null) {
  try {
    const startDate = startDateStr || `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const endDate = endDateStr || `${year}-${String(month + 1).padStart(2, '0')}-31`;

    const logRef = collection(db, 'users', userId, 'activityLog');
    const q = query(
      logRef,
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      where('hasCompletedActivity', '==', true),
      orderBy('date', 'asc')
    );

    const snapshot = await getDocs(q);
    const completedDates = snapshot.docs.map(doc => doc.data().date).sort();
    
    // Create 12 data points - calculate streak at each period
    const streakValues = [];
    
    // Parse dates for calculation
    const rangeStart = new Date(startDate + 'T00:00:00');
    const rangeEnd = new Date(endDate + 'T00:00:00');
    const totalDays = Math.ceil((rangeEnd - rangeStart) / (1000 * 60 * 60 * 24)) + 1;
    const daysPerPeriod = Math.ceil(totalDays / 12);
    
    for (let i = 0; i < 12; i++) {
      // Calculate the end date for this period
      const periodEndDate = new Date(rangeStart);
      periodEndDate.setDate(rangeStart.getDate() + ((i + 1) * daysPerPeriod) - 1);
      
      // Don't go past the range end
      if (periodEndDate > rangeEnd) {
        periodEndDate.setTime(rangeEnd.getTime());
      }
      
      let streak = 0;
      const checkDate = new Date(periodEndDate);
      
      // Check backwards from period end
      while (checkDate >= rangeStart) {
        const dateStr = `${checkDate.getFullYear()}-${String(checkDate.getMonth() + 1).padStart(2, '0')}-${String(checkDate.getDate()).padStart(2, '0')}`;
        if (completedDates.includes(dateStr)) {
          streak++;
          checkDate.setDate(checkDate.getDate() - 1);
        } else {
          break;
        }
      }
      
      streakValues.push({ value: streak });
    }
    
    return streakValues;
  } catch (error) {
    console.error('[getMonthlyStreakData] Error:', error);
    // Return zeros on error
    return Array(12).fill({ value: 0 });
  }
}

// ============================================
// PROGRAM SETUP FUNCTIONS
// ============================================

/**
 * Setup program for user (placeholder - needs full implementation)
 */
/**
 * Determine which weekly template to use based on date and seasonal rules
 */
function getWeeklyTemplateForDate(date, seasonalRules, weeklyTemplates) {
  const month = date.getMonth() + 1; // 1-12
  const day = date.getDate();
  
  // Check if date is within swimming season
  const swimStart = { month: seasonalRules.swimming.startMonth, day: seasonalRules.swimming.startDay };
  const swimEnd = { month: seasonalRules.swimming.endMonth, day: seasonalRules.swimming.endDay };
  const isSwimOn = isDateInSeason(month, day, swimStart, swimEnd);
  
  // Check if date is within biking season
  const bikeStart = { month: seasonalRules.biking.startMonth, day: seasonalRules.biking.startDay };
  const bikeEnd = { month: seasonalRules.biking.endMonth, day: seasonalRules.biking.endDay };
  const isBikeOn = isDateInSeason(month, day, bikeStart, bikeEnd);
  
  // Determine template key
  let templateKey;
  if (isSwimOn && !isBikeOn) {
    templateKey = 'swimOn_bikeOff';
  } else if (isSwimOn && isBikeOn) {
    templateKey = 'swimOn_bikeOn';
  } else if (!isSwimOn && isBikeOn) {
    templateKey = 'swimOff_bikeOn';
  } else {
    templateKey = 'swimOff_bikeOff';
  }
  
  return weeklyTemplates[templateKey];
}

/**
 * Check if a date (month, day) falls within a season range
 */
function isDateInSeason(month, day, seasonStart, seasonEnd) {
  // Handle year wrap-around (e.g., Dec 1 - Feb 28)
  if (seasonStart.month > seasonEnd.month) {
    // Season wraps around year end
    return (month > seasonStart.month || month < seasonEnd.month) ||
           (month === seasonStart.month && day >= seasonStart.day) ||
           (month === seasonEnd.month && day <= seasonEnd.day);
  } else {
    // Normal season (doesn't wrap)
    return (month > seasonStart.month && month < seasonEnd.month) ||
           (month === seasonStart.month && day >= seasonStart.day) ||
           (month === seasonEnd.month && day <= seasonEnd.day);
  }
}

/**
 * Get day name from date (sunday, monday, etc.)
 */
function getDayName(date) {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[date.getDay()];
}

/**
 * Convert 24-hour time format (HH:MM) to 12-hour AM/PM format
 * @param {string} time24 - Time in 24-hour format (e.g., "17:00", "06:30")
 * @returns {string} - Time in 12-hour format (e.g., "5:00 PM", "6:30 AM")
 */
function convertTo12Hour(time24) {
  if (!time24 || typeof time24 !== 'string') {
    return '6:00 AM'; // Default
  }
  
  const [hours, minutes] = time24.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) {
    return '6:00 AM'; // Default if invalid
  }
  
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const minutesStr = minutes.toString().padStart(2, '0');
  
  // No leading zero on hours - use hours12 directly (not padded)
  return `${hours12}:${minutesStr} ${period}`;
}

function hasMacrocycleActivityMix(template) {
  // Check template-level weekly_activity_mix (applies to all macrocycles)
  const rootDa = template.weekly_activity_mix?.day_assignments;
  if (rootDa && Object.values(rootDa).some((v) => (Array.isArray(v) ? v.length > 0 : !!v))) {
    return true;
  }
  // Check per-macrocycle weekly_activity_mix
  const macros = template.macrocycles || [];
  return macros.some((m) => {
    const da = m.weekly_activity_mix?.day_assignments;
    return da && Object.values(da).some((v) => (Array.isArray(v) ? v.length > 0 : !!v));
  });
}

function collectMesocyclesFromMacros(macros) {
  const all = [];
  for (const m of macros) {
    if (Array.isArray(m.mesocycles)) {
      all.push(...m.mesocycles);
    }
  }
  return all;
}

/**
 * Resolve the calendar date range for a macrocycle from its start_month/end_month.
 * Handles year wrapping (e.g., start_month=11, end_month=2 → Nov 1 of refYear to Feb 28 of refYear+1).
 * refYear is the "program year" anchor — typically the current year or the year the program spans.
 */
function macroMonthRange(macro, refYear) {
  const sm = parseInt(macro.start_month ?? macro.startMonth, 10);
  const em = parseInt(macro.end_month ?? macro.endMonth, 10);
  if (!sm || !em || isNaN(sm) || isNaN(em)) return null;

  let startYear = refYear;
  let endYear = refYear;

  if (em < sm) {
    endYear = refYear + 1;
  }

  const startDate = `${startYear}-${String(sm).padStart(2, '0')}-01`;
  const lastDay = new Date(endYear, em, 0).getDate();
  const endDate = `${endYear}-${String(em).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { startDate, endDate };
}

/**
 * Determine the "program year" from the macrocycles' month layout and a reference date.
 * For a program running Aug 2025 – Jul 2026, if today is Feb 2026, the program year is 2025
 * (the year the first macrocycle starts). This lets macroMonthRange resolve all dates correctly.
 */
function inferProgramYear(macros, refDate) {
  const refYear = refDate.getFullYear();
  const refMonth = refDate.getMonth() + 1; // 1-12

  const startMonths = macros.map((m) => parseInt(m.start_month ?? m.startMonth, 10)).filter((v) => v && !isNaN(v));
  if (startMonths.length === 0) return refYear;

  const earliestStart = Math.min(...startMonths);

  if (refMonth < earliestStart) return refYear - 1;
  return refYear;
}

function findActiveMacrocycle(macros, date) {
  const dateStr = formatDateStr(date);
  const programYear = inferProgramYear(macros, date);

  // 1. Try structured month fields (preferred)
  for (const m of macros) {
    const sm = m.start_month ?? m.startMonth;
    const em = m.end_month ?? m.endMonth;
    if (sm && em) {
      const range = macroMonthRange(m, programYear);
      if (range && dateStr >= range.startDate && dateStr <= range.endDate) return m;
    }
  }

  // 2. Fall back to explicit startDate/endDate (computed or stored)
  for (const m of macros) {
    if (m.startDate && m.endDate && dateStr >= m.startDate && dateStr <= m.endDate) return m;
  }

  return null;
}

function findActiveMesocycle(mesos, macro, date) {
  const dateStr = formatDateStr(date);
  const macroId = macro?.macrocycle_id || macro?.macrocycleId || macro?.id;
  const candidates = mesos.filter((ms) => {
    const mid = ms.macrocycle_id || ms.macrocycleId;
    return mid === macroId;
  });
  for (const ms of candidates) {
    if (ms.startDate && ms.endDate && dateStr >= ms.startDate && dateStr <= ms.endDate) return ms;
  }
  return candidates[candidates.length - 1] || null;
}

const ACTIVITY_TYPE_MAP = {
  strength: { activityType: 'strength_workout', name: 'Strength', duration: 50 },
  game: { activityType: 'game', name: 'Game', duration: 120 },
  practice: { activityType: 'practice', name: 'Practice', duration: 120 },
  practice_or_game: { activityType: 'practice', name: 'Practice / Game', duration: 120 },
  drills: { activityType: 'drills', name: 'Drills', duration: 60 },
  cardio: { activityType: 'endurance', name: 'Cardio', duration: 45 },
  running: { activityType: 'endurance', name: 'Run', duration: 45 },
  swimming: { activityType: 'endurance', name: 'Swim', duration: 60 },
  biking: { activityType: 'endurance', name: 'Bike', duration: 60 },
  yoga: { activityType: 'recovery', name: 'Yoga', duration: 45 },
  hiit: { activityType: 'hiit', name: 'HIIT', duration: 30 },
  walking: { activityType: 'endurance', name: 'Walk', duration: 30 },
};

function mapAdminActivityType(type) {
  const key = Array.isArray(type) ? type[0] : type;
  if (!key || typeof key !== 'string') return { activityType: 'rest', name: 'Rest', duration: 0 };
  const t = key.toLowerCase().trim();
  return ACTIVITY_TYPE_MAP[t] || { activityType: t, name: t.charAt(0).toUpperCase() + t.slice(1), duration: 60 };
}

export async function setupProgramForUser(userId, programTemplate) {
  try {
    console.log('[setupProgramForUser] Setting up program:', programTemplate.name);
    console.log('[setupProgramForUser] Template macrocycles:', (programTemplate.macrocycles || []).map((m) => {
      const id = m.macrocycle_id || m.macrocycleId || m.id;
      const sm = m.start_month ?? m.startMonth;
      const em = m.end_month ?? m.endMonth;
      return `${id} (${m.name}): months ${sm}-${em}`;
    }));
    console.log('[setupProgramForUser] Template-level weekly_activity_mix:', !!programTemplate.weekly_activity_mix);
    
    // 0. Clean up existing future scheduled activities from any prior program
    const todayStr = formatDateStr(new Date());
    const existingRef = collection(db, 'users', userId, 'scheduledActivities');
    const futureQ = query(existingRef, where('scheduledDate', '>=', todayStr), where('status', '==', 'scheduled'));
    const futureSnap = await getDocs(futureQ);
    if (futureSnap.size > 0) {
      const cleanupBatch = writeBatch(db);
      futureSnap.docs.forEach((d) => cleanupBatch.delete(d.ref));
      await cleanupBatch.commit();
      console.log(`[setupProgramForUser] Cleared ${futureSnap.size} old future activities`);
    }

    const batch = writeBatch(db);
    const programId = programTemplate.programId || `program_${Date.now()}`;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const numWeeks = programTemplate.durationWeeks || 52;
    
    // 1. Create program document (including weeklyTemplates for future resets)
    const programRef = doc(db, 'users', userId, 'programs', programId);
    const programDoc = {
      id: programId,
      name: programTemplate.name || null,
      description: programTemplate.description || null,
      programStartDate: programTemplate.programStartDate || formatDateStr(today),
      enrolledDate: formatDateStr(today),
      durationWeeks: numWeeks,
      difficulty: programTemplate.difficulty || null,
      workoutsPerWeek: programTemplate.workoutsPerWeek || null,
      strengthSessionsPerWeek: programTemplate.strengthSessionsPerWeek || null,
      enduranceSessionsPerWeek: programTemplate.enduranceSessionsPerWeek || null,
      goals: programTemplate.goals || [],
      equipment: programTemplate.equipment || [],
      keyEvents: programTemplate.keyEvents || [],
      seasonalRules: programTemplate.seasonalRules || {},
      weeklyTemplates: programTemplate.weeklyTemplates || null,
      templateVersion: programTemplate.templateVersion || null,
      status: 'active',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    batch.set(programRef, programDoc);
    
    // 2. Create macrocycles and mesocycles (compute dates if missing)
    // Admin stores mesocycles in TWO places:
    //   a) nested: macrocycle.mesocycles (summary refs from fixtures)
    //   b) top-level: programTemplate.mesocycles (full data, linked via macrocycle_id)
    // We merge both, preferring full top-level data.
    const topLevelMesos = Array.isArray(programTemplate.mesocycles) ? programTemplate.mesocycles : [];
    const createdMesoIds = new Set();
    let firstMacroId = null;
    let firstMesoId = null;

    const macros = programTemplate.macrocycles || [];
    const programYear = macros.length > 0 ? inferProgramYear(macros, today) : today.getFullYear();

    if (macros.length > 0) {
      // Compute macrocycle date ranges from calendar months (start_month/end_month).
      // Falls back to sequential stacking only if no structured months exist.
      let macroRunningDate = new Date(today);

      for (const macrocycle of macros) {
        const macroId = macrocycle.macrocycleId || macrocycle.macrocycle_id || macrocycle.id;
        if (!macroId) continue;
        if (!firstMacroId) firstMacroId = macroId;

        let macroStart, macroEnd;
        const monthRange = macroMonthRange(macrocycle, programYear);
        if (monthRange) {
          macroStart = monthRange.startDate;
          macroEnd = monthRange.endDate;
        } else {
          const macroDurationWeeks = macrocycle.duration_weeks || macrocycle.durationWeeks || 12;
          macroStart = macrocycle.startDate || formatDateStr(macroRunningDate);
          macroEnd = macrocycle.endDate;
          if (!macroEnd) {
            const endD = new Date(macroRunningDate);
            endD.setDate(endD.getDate() + (macroDurationWeeks * 7) - 1);
            macroEnd = formatDateStr(endD);
          }
        }

        macrocycle.startDate = macroStart;
        macrocycle.endDate = macroEnd;

        const macrocycleRef = doc(db, 'users', userId, 'macrocycles', macroId);
        const smVal = parseInt(macrocycle.start_month ?? macrocycle.startMonth, 10) || null;
        const emVal = parseInt(macrocycle.end_month ?? macrocycle.endMonth, 10) || null;
        batch.set(macrocycleRef, {
          id: macroId,
          programId,
          name: macrocycle.name || null,
          order: macrocycle.order ?? null,
          startDate: macroStart,
          endDate: macroEnd,
          start_month: smVal,
          end_month: emVal,
          startMonth: smVal,
          endMonth: emVal,
          durationMonths: macrocycle.durationMonths ?? macrocycle.duration_months ?? null,
          phase: macrocycle.phase || null,
          focus: macrocycle.focus || null,
          weekly_activity_mix: macrocycle.weekly_activity_mix || null,
          seasonalFocus: macrocycle.seasonalFocus || {},
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        // Gather mesocycles for this macrocycle from BOTH sources
        const nestedMesos = macrocycle.mesocycles || [];
        const linkedMesos = topLevelMesos.filter((m) => {
          const mid = m.macrocycle_id || m.macrocycleId;
          return mid === macroId;
        });

        // Build a merged list: prefer top-level (full data) over nested (summary)
        const mesoMap = new Map();
        for (const m of nestedMesos) {
          const id = m.mesocycleId || m.mesocycle_id || m.id;
          if (id) mesoMap.set(id, m);
        }
        for (const m of linkedMesos) {
          const id = m.mesocycleId || m.mesocycle_id || m.id;
          if (id) mesoMap.set(id, { ...mesoMap.get(id), ...m });
        }

        // Sort by order
        const mergedMesos = [...mesoMap.values()].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

        let mesoRunningDate = new Date(macroStart + 'T00:00:00');
        for (const mesocycle of mergedMesos) {
          const mesoId = mesocycle.mesocycleId || mesocycle.mesocycle_id || mesocycle.id;
          if (!mesoId || createdMesoIds.has(mesoId)) continue;
          createdMesoIds.add(mesoId);
          if (!firstMesoId) firstMesoId = mesoId;

          const mesoWeeks = mesocycle.totalWeeks || mesocycle.duration_weeks || mesocycle.durationWeeks || 4;
          let mesoStart = mesocycle.startDate || formatDateStr(mesoRunningDate);
          let mesoEnd = mesocycle.endDate;
          if (!mesoEnd) {
            const endD = new Date(mesoRunningDate);
            endD.setDate(endD.getDate() + (mesoWeeks * 7) - 1);
            mesoEnd = formatDateStr(endD);
          }

          // Write computed dates back to in-memory objects so findActiveMesocycle works
          mesocycle.startDate = mesoStart;
          mesocycle.endDate = mesoEnd;

          const mesocycleRef = doc(db, 'users', userId, 'mesocycles', mesoId);
          batch.set(mesocycleRef, {
            id: mesoId,
            programId,
            name: mesocycle.name || null,
            order: mesocycle.order ?? null,
            startDate: mesoStart,
            endDate: mesoEnd,
            totalWeeks: mesoWeeks,
            strengthPhase: mesocycle.strengthPhase || mesocycle.strength_phase || null,
            phaseDescription: mesocycle.phaseDescription || mesocycle.description || null,
            repRanges: mesocycle.repRanges || null,
            intensity: mesocycle.intensity || null,
            workoutsPerWeek: mesocycle.workoutsPerWeek || null,
            notes: mesocycle.notes || null,
            macrocycleId: macroId,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });

          mesoRunningDate.setDate(mesoRunningDate.getDate() + (mesoWeeks * 7));
        }

        // Advance running date for next macrocycle
        macroRunningDate = new Date(macroEnd + 'T00:00:00');
        macroRunningDate.setDate(macroRunningDate.getDate() + 1);
      }
    }

    console.log(`[setupProgramForUser] Created macrocycles/mesocycles. firstMacro=${firstMacroId}, firstMeso=${firstMesoId}, totalMesos=${createdMesoIds.size}`);
    console.log('[setupProgramForUser] programYear:', programYear, '| today:', formatDateStr(today));
    console.log('[setupProgramForUser] Computed macrocycle date ranges:', macros.map((m) => {
      const id = m.macrocycle_id || m.macrocycleId || m.id;
      return `${id}: ${m.startDate} → ${m.endDate}`;
    }));
    const activeMacroCheck = findActiveMacrocycle(macros, today);
    console.log('[setupProgramForUser] Active macrocycle for today:', activeMacroCheck?.name || activeMacroCheck?.macrocycle_id || 'NONE');
    
    // 3. Generate and schedule activities: from generatedSchedule (build-from-hierarchy) or weeklyTemplates
    let activitiesCreated = 0;
    const generatedSchedule = programTemplate.generatedSchedule;

    if (generatedSchedule && typeof generatedSchedule === 'object' && Object.keys(generatedSchedule).length > 0) {
      // Use pre-built calendar from microcycle generator (mesoWeekIndex, mesocycleId for progression)
      for (const [scheduledDate, dayActivities] of Object.entries(generatedSchedule)) {
        if (!Array.isArray(dayActivities)) continue;
        for (const activity of dayActivities) {
          const activityRef = doc(collection(db, 'users', userId, 'scheduledActivities'));
          batch.set(activityRef, {
            scheduledDate,
            scheduledTime: null,
            activityType: activity.activityType || 'strength_workout',
            source: 'program',
            programId,
            workoutTemplateId: activity.workoutTemplateId || null,
            name: activity.name || null,
            description: activity.description || null,
            estimatedDuration: activity.estimatedDuration ?? 60,
            duration: activity.estimatedDuration ?? 60,
            status: 'scheduled',
            weekNumber: activity.weekNumber ?? null,
            mesoWeekIndex: activity.mesoWeekIndex ?? null,
            mesocycleId: activity.mesocycleId ?? null,
            weekLabel: activity.weekLabel ?? null,
            intensityModifier: activity.intensityModifier ?? null,
            volumeModifier: activity.volumeModifier ?? null,
            rir: activity.rir ?? null,
            isEdited: false,
            completedAt: null,
            completionSource: null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          activitiesCreated++;
        }
      }
      console.log('[setupProgramForUser] Populated schedule from generatedSchedule:', activitiesCreated, 'activities');

    } else if (hasMacrocycleActivityMix(programTemplate)) {
      // Build schedule from macrocycles' weekly_activity_mix.day_assignments (admin-defined).
      // Schedule starts from TODAY — user joins a program already in progress.
      const mesos = programTemplate.mesocycles || collectMesocyclesFromMacros(macros);

      // Pre-fetch workout template names for strength activities
      const allTemplateIds = new Set();
      for (const m of mesos) {
        for (const tid of (m.workout_templates || [])) {
          if (tid) allTemplateIds.add(tid);
        }
      }
      const templateNameMap = {};
      if (allTemplateIds.size > 0) {
        const fetches = [...allTemplateIds].map(async (tid) => {
          try {
            const snap = await getDoc(doc(db, 'workoutTemplates', tid));
            if (snap.exists()) {
              templateNameMap[tid] = snap.data().name || tid;
            }
          } catch (e) { /* skip */ }
        });
        await Promise.all(fetches);
        console.log(`[setupProgramForUser] Loaded ${Object.keys(templateNameMap).length} workout template names`);
      }

      // Compute scheduling end from the latest macrocycle endDate that covers today or later
      let latestEnd = null;
      for (const m of macros) {
        if (m.endDate) {
          const end = new Date(m.endDate + 'T00:00:00');
          if (!latestEnd || end > latestEnd) latestEnd = end;
        }
      }
      const fallbackEnd = new Date(today);
      fallbackEnd.setDate(fallbackEnd.getDate() + (numWeeks * 7));
      const endDate = latestEnd && latestEnd > today ? latestEnd : fallbackEnd;
      // Add 1 day so the loop includes the last macrocycle date
      endDate.setDate(endDate.getDate() + 1);

      let currentDate = new Date(today);

      let weekNumber = 1;
      let lastSunday = new Date(currentDate);
      lastSunday.setDate(lastSunday.getDate() - lastSunday.getDay());
      const strengthRotation = {};

      while (currentDate < endDate) {
        const dayName = getDayName(currentDate);
        const scheduledDate = formatDateStr(currentDate);

        const currentSunday = new Date(currentDate);
        currentSunday.setDate(currentSunday.getDate() - currentSunday.getDay());
        if (currentSunday > lastSunday) {
          weekNumber++;
          lastSunday = currentSunday;
        }

        const macro = findActiveMacrocycle(macros, currentDate);
        if (!macro) {
          // Date falls outside all macrocycle ranges (end of annual cycle) — skip
          currentDate.setDate(currentDate.getDate() + 1);
          continue;
        }
        const mix = macro.weekly_activity_mix || programTemplate.weekly_activity_mix;
        if (!mix?.day_assignments) {
          throw new Error(`[setupProgramForUser] No weekly_activity_mix.day_assignments for macrocycle "${macro.name || macro.macrocycle_id}". Date: ${scheduledDate}`);
        }
        const dayAssignments = mix.day_assignments;

        {
          const raw = dayAssignments[dayName];
          const acts = Array.isArray(raw) ? raw : (raw ? [raw] : []);

          for (const actType of acts) {
            const lower = (actType || '').toLowerCase();
            if (lower === 'rest' || lower === 'recovery') continue;

            const mapped = mapAdminActivityType(lower);
            let workoutTemplateId = null;

            if (mapped.activityType === 'strength_workout') {
              const meso = findActiveMesocycle(mesos, macro, currentDate);
              const pool = meso?.workout_templates || [];
              if (pool.length > 0) {
                const mesoKey = meso?.id || meso?.mesocycle_id || 'default';
                if (!strengthRotation[mesoKey]) strengthRotation[mesoKey] = 0;
                workoutTemplateId = pool[strengthRotation[mesoKey] % pool.length];
                strengthRotation[mesoKey]++;
              }
            }

            const actName = (workoutTemplateId && templateNameMap[workoutTemplateId])
              ? templateNameMap[workoutTemplateId]
              : mapped.name;

            const activityRef = doc(collection(db, 'users', userId, 'scheduledActivities'));
            batch.set(activityRef, {
              scheduledDate,
              scheduledTime: null,
              activityType: mapped.activityType,
              source: 'program',
              programId,
              workoutTemplateId,
              name: actName,
              description: null,
              estimatedDuration: mapped.duration,
              duration: mapped.duration,
              status: 'scheduled',
              weekNumber,
              isEdited: false,
              completedAt: null,
              completionSource: null,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            });
            activitiesCreated++;
          }
        }

        currentDate.setDate(currentDate.getDate() + 1);
      }
      console.log('[setupProgramForUser] Built schedule from weekly_activity_mix:', activitiesCreated, 'activities');

    } else if (programTemplate.weeklyTemplates && programTemplate.seasonalRules) {
      // Legacy: generate from weeklyTemplates + seasonalRules + template rotation
      const endDate = new Date(today);
      endDate.setDate(endDate.getDate() + (numWeeks * 7));

      let currentDate = new Date(today);

      let weekNumber = 1;
      let lastSunday = new Date(currentDate);
      lastSunday.setDate(lastSunday.getDate() - lastSunday.getDay());

      while (currentDate < endDate) {
        const dayName = getDayName(currentDate);

        const currentSunday = new Date(currentDate);
        currentSunday.setDate(currentSunday.getDate() - currentSunday.getDay());
        if (currentSunday > lastSunday) {
          weekNumber++;
          lastSunday = currentSunday;
        }

        const weeklyTemplate = getWeeklyTemplateForDate(
          currentDate,
          programTemplate.seasonalRules,
          programTemplate.weeklyTemplates
        );

        if (weeklyTemplate && weeklyTemplate[dayName]) {
          const dayActivities = weeklyTemplate[dayName];

          for (const activity of dayActivities) {
            const activityRef = doc(collection(db, 'users', userId, 'scheduledActivities'));
            const scheduledDate = formatDateStr(currentDate);

            let workoutTemplateId = activity.workoutTemplateId || null;
            if (activity.workoutTemplateIdAlt && weekNumber % 2 === 0) {
              workoutTemplateId = activity.workoutTemplateIdAlt;
            }

            batch.set(activityRef, {
              scheduledDate,
              scheduledTime: null,
              activityType: activity.activityType || 'endurance',
              source: 'program',
              programId,
              workoutTemplateId,
              name: activity.name,
              description: activity.description || null,
              estimatedDuration: activity.estimatedDuration || 60,
              duration: activity.estimatedDuration || 60,
              status: 'scheduled',
              weekNumber,
              isEdited: false,
              completedAt: null,
              completionSource: null,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            });
            activitiesCreated++;
          }
        }

        currentDate.setDate(currentDate.getDate() + 1);
      }
    } else {
      console.warn('[setupProgramForUser] No scheduling data found (no generatedSchedule, no weekly_activity_mix, no weeklyTemplates)');
    }
    
    // 4. Update user profile with program references
    //    Use findActiveMacrocycle (calendar-month aware) to pick the current macro/meso.
    const userRef = doc(db, 'users', userId);
    const activeMacro = findActiveMacrocycle(macros, today);
    const activeMacroId = activeMacro
      ? (activeMacro.macrocycleId || activeMacro.macrocycle_id || activeMacro.id)
      : firstMacroId;

    let activeMesoId = firstMesoId;
    if (activeMacro) {
      const allMesos = programTemplate.mesocycles || collectMesocyclesFromMacros(macros);
      const found = findActiveMesocycle(allMesos, activeMacro, today);
      if (found) {
        activeMesoId = found.mesocycleId || found.mesocycle_id || found.id || firstMesoId;
      }
    }

    console.log(`[setupProgramForUser] Active cycle selection: macro=${activeMacroId}, meso=${activeMesoId} (today=${todayStr})`);

    const templateVersion = programTemplate.templateVersion || null;

    batch.update(userRef, {
      currentProgramId: programId,
      currentMacrocycleId: activeMacroId || null,
      currentMesocycleId: activeMesoId || null,
      ...(templateVersion ? { templateVersion } : {}),
      updatedAt: serverTimestamp(),
    });
    
    await batch.commit();
    
    console.log(`[setupProgramForUser] Created ${activitiesCreated} activities over ${numWeeks} weeks`);
    return { success: true, activitiesCreated };
    
  } catch (error) {
    console.error('[setupProgramForUser] Error:', error);
    throw error;
  }
}

// ============================================
// VB CLUB SEASON PROGRAM SETUP
// ============================================

/**
 * VB Club Season workout templates
 * 3 workouts per week: Lower Body, Upper Body, Explosion & Agility
 * Updated 2026-01-07 with:
 * - Day C (Explosion & Agility) added
 * - Finisher rotation across Day A, B, C
 * - No duplicate exercises rule
 */
const VB_CLUB_TEMPLATES = {
  tuesday: {
    name: 'VB Club – Day A – Lower Body Strength',
    templateId: 'vb_club_day_a',
    type: 'strength_workout',
    estimatedDuration: 50,
    time: '16:00',
    finisher: 'vb_posterior_chain_finisher',
  },
  thursday: {
    name: 'VB Club – Day B – Upper Body Strength',
    templateId: 'vb_club_day_b',
    type: 'strength_workout',
    estimatedDuration: 45,
    time: '16:00',
    finisher: 'vb_core_rotation_finisher',
  },
  saturday: {
    name: 'VB Club – Day C – Explosion & Agility',
    templateId: 'vb_club_day_c',
    type: 'strength_workout',
    estimatedDuration: 45,
    time: '10:00',
    finisher: 'vb_anti_rotation_finisher',
  },
  sunday: {
    name: 'VB Game Day',
    type: 'game',
    estimatedDuration: 120,
    time: '09:00',
  },
  monday: {
    name: 'VB Practice',
    type: 'practice',
    estimatedDuration: 120,
    time: '17:30',
  },
  wednesday: {
    name: 'VB Practice',
    type: 'practice',
    estimatedDuration: 120,
    time: '17:30',
  },
};

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/**
 * Helper: Format date as YYYY-MM-DD
 */
function formatDateStr(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Setup VB Club Season program for a user
 * Call this after onboarding when persona is 'court_sports'
 * 
 * @param {string} userId - Firebase user ID
 * @param {number} numWeeks - Number of weeks to schedule (default 8)
 * @returns {Promise<{success: boolean, activitiesCreated: number}>}
 */
export async function setupVBClubProgram(userId, numWeeks = 8) {
  try {
    console.log(`[setupVBClubProgram] Setting up VB Club program for user: ${userId}`);
    
    const batch = writeBatch(db);
    
    // 1. Create program document
    const programId = 'vb_club_2026';
    const programRef = doc(db, 'users', userId, 'programs', programId);
    batch.set(programRef, {
      id: programId,
      name: 'VB Club Season 2026',
      persona: 'court_sports',
      personaId: 'vb_player_13_18',
      startDate: formatDateStr(new Date()),
      status: 'active',
      description: 'Volleyball off-season strength training: Lower Body, Upper Body, and Explosion & Agility',
      workoutsPerWeek: 3,
      schedule: {
        sunday: 'game',
        monday: 'practice',
        tuesday: 'strength - lower body',
        wednesday: 'practice',
        thursday: 'strength - upper body',
        saturday: 'plyometric - explosion & agility',
      },
      templateVersion: TEMPLATE_VERSION,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    // 2. Create macrocycle document
    const macrocycleId = 'winter_spring_club_2026';
    const macrocycleRef = doc(db, 'users', userId, 'macrocycles', macrocycleId);
    batch.set(macrocycleRef, {
      id: macrocycleId,
      name: 'Club Season 2026 – Off-Season Building',
      phase: 'off_season',
      phaseName: 'Power & Functional Strength',
      startDate: '2026-01-01',
      endDate: '2026-05-31',
      goals: [
        'Maximize vertical jump',
        'Improve lateral quickness and agility',
        'Build reactive strength and explosion',
        'Develop court movement speed',
        'Peak for club tournaments',
      ],
      weeklyStructure: {
        sunday: 'VB Game Day',
        monday: 'VB Practice',
        tuesday: 'Day A – Lower Body Strength (vb_posterior_chain_finisher)',
        wednesday: 'VB Practice',
        thursday: 'Day B – Upper Body Strength (vb_core_rotation_finisher)',
        saturday: 'Day C – Explosion & Agility (vb_anti_rotation_finisher)',
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    // 3. Generate and schedule activities
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay()); // Start from Sunday
    
    let activitiesCreated = 0;
    
    for (let week = 0; week < numWeeks; week++) {
      for (let day = 0; day < 7; day++) {
        const date = new Date(startOfWeek);
        date.setDate(startOfWeek.getDate() + (week * 7) + day);
        
        const dayName = DAY_NAMES[date.getDay()];
        const template = VB_CLUB_TEMPLATES[dayName];
        
        if (template && template.type !== 'rest') {
          const activityRef = doc(collection(db, 'users', userId, 'scheduledActivities'));
          batch.set(activityRef, {
            scheduledDate: formatDateStr(date),
            scheduledTime: convertTo12Hour(template.time || '06:00'),
            activityType: template.type,
            source: 'program',
            workoutTemplateId: template.templateId || null,
            name: template.name,
            estimatedDuration: template.estimatedDuration,
            status: 'scheduled',
            week: week + 1,
            isEdited: false,
            completedAt: null,
            completionSource: null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          activitiesCreated++;
        }
      }
    }
    
    // 4. Update user profile with program references
    const userRef = doc(db, 'users', userId);
    batch.update(userRef, {
      currentProgramId: programId,
      currentMacrocycleId: macrocycleId,
      currentMesocycleWeek: 1,
      updatedAt: serverTimestamp(),
    });
    
    await batch.commit();
    
    console.log(`[setupVBClubProgram] Created ${activitiesCreated} activities over ${numWeeks} weeks`);
    return { success: true, activitiesCreated };
    
  } catch (error) {
    console.error('[setupVBClubProgram] Error:', error);
    throw error;
  }
}

/**
 * VB School Season (Fall In-Season) workout templates
 * 2 workouts per week: Maintenance focus
 * Morning strength Mon/Wed before school, practice after school
 * Updated 2026-01-07 with:
 * - Fixed duplicate exercises (mini_loop_band_glute_bridge, loop_band_face_pull)
 * - No leg_swings in warmup
 * - Finisher rotation
 */
const VB_SCHOOL_TEMPLATES = {
  monday: {
    name: 'VB In-Season – Day A – Lower Body Maintenance',
    templateId: 'vb_inseason_day_a',
    type: 'strength_workout',
    estimatedDuration: 35,
    time: '06:30', // Before school
    finisher: 'vb_inseason_core_finisher',
  },
  wednesday: {
    name: 'VB In-Season – Day B – Upper + Full Body',
    templateId: 'vb_inseason_day_b',
    type: 'strength_workout',
    estimatedDuration: 35,
    time: '06:30',
    finisher: 'vb_core_rotation_finisher',
  },
  // Practice markers (after school)
  tuesday: {
    name: 'VB Practice + Game',
    type: 'practice',
    estimatedDuration: 180,
    time: '15:30',
  },
  thursday: {
    name: 'VB Practice',
    type: 'practice',
    estimatedDuration: 120,
    time: '15:30',
  },
  friday: {
    name: 'VB Practice + Game',
    type: 'practice',
    estimatedDuration: 180,
    time: '15:30',
  },
  saturday: {
    name: 'Rest / Recovery',
    type: 'rest',
    estimatedDuration: 0,
    time: null,
  },
  sunday: {
    name: 'Rest Day',
    type: 'rest',
    estimatedDuration: 0,
    time: null,
  },
};

/**
 * Setup VB School Season (Fall) program
 * Morning workouts Mon/Wed before practice, Saturday plyometrics
 * 
 * @param {string} userId - Firebase user ID
 * @param {number} numWeeks - Number of weeks to schedule (default 12)
 */
export async function setupVBSchoolProgram(userId, numWeeks = 12) {
  try {
    console.log(`[setupVBSchoolProgram] Setting up VB School program for user: ${userId}`);
    
    const batch = writeBatch(db);
    
    // 1. Create program document
    const programId = 'vb_school_2026';
    const programRef = doc(db, 'users', userId, 'programs', programId);
    batch.set(programRef, {
      id: programId,
      name: 'VB School Season 2026',
      persona: 'court_sports',
      personaId: 'vb_player_13_18',
      startDate: formatDateStr(new Date()),
      status: 'active',
      description: 'Volleyball in-season maintenance: 2 AM strength sessions per week before school',
      workoutsPerWeek: 2,
      schedule: {
        monday: 'am_strength - lower body maintenance',
        wednesday: 'am_strength - upper + full body',
        tuesday: 'practice_game',
        thursday: 'practice',
        friday: 'practice_game',
        saturday: 'rest',
        sunday: 'rest',
      },
      templateVersion: TEMPLATE_VERSION,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    // 2. Create macrocycle document
    const macrocycleId = 'fall_school_2026';
    const macrocycleRef = doc(db, 'users', userId, 'macrocycles', macrocycleId);
    batch.set(macrocycleRef, {
      id: macrocycleId,
      name: 'School Season 2026 – In-Season Maintenance',
      phase: 'in-season',
      phaseName: 'In-Season Maintenance',
      startDate: '2026-08-15',
      endDate: '2026-11-15',
      goals: [
        'Maintain strength gains from off-season',
        'Support game-day performance',
        'Prevent overuse injuries',
        'Manage training fatigue around games',
      ],
      weeklyStructure: {
        monday: 'Day A – Lower Body Maintenance (AM before school)',
        wednesday: 'Day B – Upper + Full Body (AM before school)',
        tuesday: 'Practice + Game',
        thursday: 'Practice',
        friday: 'Practice + Game',
        saturday: 'Rest / Recovery',
        sunday: 'Rest',
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    // 3. Generate and schedule activities
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    
    let activitiesCreated = 0;
    
    for (let week = 0; week < numWeeks; week++) {
      for (let day = 0; day < 7; day++) {
        const date = new Date(startOfWeek);
        date.setDate(startOfWeek.getDate() + (week * 7) + day);
        
        const dayName = DAY_NAMES[date.getDay()];
        const template = VB_SCHOOL_TEMPLATES[dayName];
        
        if (template && template.type !== 'rest') {
          const activityRef = doc(collection(db, 'users', userId, 'scheduledActivities'));
          batch.set(activityRef, {
            scheduledDate: formatDateStr(date),
            scheduledTime: convertTo12Hour(template.time || '06:00'),
            activityType: template.type,
            source: 'program',
            workoutTemplateId: template.templateId || null,
            name: template.name,
            estimatedDuration: template.estimatedDuration,
            status: 'scheduled',
            week: week + 1,
            isEdited: false,
            completedAt: null,
            completionSource: null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          activitiesCreated++;
        }
      }
    }
    
    // 4. Update user profile with program references
    const userRef = doc(db, 'users', userId);
    batch.update(userRef, {
      currentProgramId: programId,
      currentMacrocycleId: macrocycleId,
      currentMesocycleWeek: 1,
      updatedAt: serverTimestamp(),
    });
    
    await batch.commit();
    
    console.log(`[setupVBSchoolProgram] Created ${activitiesCreated} activities over ${numWeeks} weeks`);
    return { success: true, activitiesCreated };
    
  } catch (error) {
    console.error('[setupVBSchoolProgram] Error:', error);
    throw error;
  }
}

// ============================================
// UNIFIED PROGRAM SETUP (for onboarding)
// ============================================

// ============================================
// MULTI-SPORT PROGRAM SETUP
// ============================================

/**
 * Calculate which week we are within a season
 * Seasons are approximately 12 weeks each
 * 
 * @param {string} season - 'winter', 'spring', 'summer', 'fall'
 * @returns {number} - Current week within the season (1-12)
 */
function getWeekInSeason(season) {
  const today = new Date();
  const month = today.getMonth(); // 0-11
  
  // Approximate start of each season
  const seasonStarts = {
    winter: new Date(today.getFullYear(), 11, 1), // Dec 1
    spring: new Date(today.getFullYear(), 2, 1),  // Mar 1
    summer: new Date(today.getFullYear(), 5, 1),  // Jun 1
    fall: new Date(today.getFullYear(), 8, 1),    // Sep 1
  };
  
  // Handle year wrap for winter (Dec is previous year's winter start)
  if (season === 'winter' && month < 2) {
    seasonStarts.winter = new Date(today.getFullYear() - 1, 11, 1);
  }
  
  const seasonStart = seasonStarts[season];
  const weeksSinceStart = Math.floor((today - seasonStart) / (7 * 24 * 60 * 60 * 1000));
  
  // Clamp to 0-11 (weeks 1-12)
  return Math.min(Math.max(weeksSinceStart, 0), 11) + 1;
}

/**
 * Setup Multi-Sport program
 * Drops user into current position in the seasonal cycle
 * 
 * @param {string} userId - Firebase user ID
 * @param {string} season - 'winter', 'spring', 'summer', 'fall'
 * @param {number} numWeeks - Number of weeks to schedule forward
 */
export async function setupMultiSportProgram(userId, season, numWeeks = 12) {
  try {
    console.log(`[setupMultiSportProgram] Setting up ${season} program for user: ${userId}`);
    
    // Fetch template from Firestore
    const template = await fetchProgramTemplate('multi_sport_sprint');
    if (!template) {
      throw new Error('Multi-sport program template not found in Firestore');
    }
    if (!template.programStartDate) {
      template.programStartDate = formatDateStr(new Date());
    }
    if (!template.programId) {
      template.programId = 'multi_sport_sprint';
    }
    
    const result = await setupProgramForUser(userId, template);
    const currentWeekInSeason = getWeekInSeason(season);
    
    return { 
      ...result, 
      currentWeekInSeason 
    };
    
  } catch (error) {
    console.error('[setupMultiSportProgram] Error:', error);
    throw error;
  }
}

/**
 * Fetch a program template from Firestore.
 * Tries programTemplates/{id} first, then programs/{id}.
 * Returns the template data or null if not found.
 *
 * @param {string} templateId - The Firestore document ID
 * @returns {Promise<object|null>}
 */
export async function fetchProgramTemplate(templateId) {
  try {
    const [templateSnap, programSnap] = await Promise.all([
      getDoc(doc(db, 'programTemplates', templateId)),
      getDoc(doc(db, 'programs', templateId)),
    ]);
    if (templateSnap.exists()) {
      console.log(`[fetchProgramTemplate] Found in programTemplates/${templateId}`);
      return { id: templateSnap.id, ...templateSnap.data() };
    }
    if (programSnap.exists()) {
      console.log(`[fetchProgramTemplate] Found in programs/${templateId}`);
      return { id: programSnap.id, ...programSnap.data() };
    }
    console.warn(`[fetchProgramTemplate] No template found for: ${templateId}`);
    return null;
  } catch (error) {
    console.error(`[fetchProgramTemplate] Error fetching ${templateId}:`, error);
    return null;
  }
}

/**
 * Setup program based on persona selection.
 * Fetches the program template from Firestore and uses setupProgramForUser.
 * Season detection maps program IDs to the correct Firestore template.
 *
 * @param {string} userId - User ID
 * @param {string} programId - Program ID from onboarding selection (e.g. court_volleyball, multi_sport_sprint)
 * @returns {object} - { success: boolean, programType: string, activitiesCreated: number }
 */
export async function setupProgramByPersona(userId, programId) {
  console.log(`[setupProgramByPersona] Setting up program: ${programId}`);

  const month = new Date().getMonth(); // 0-11
  let templateId = programId;
  let programType = programId;
  let season = null;

  // Court: Volleyball
  if (programId === 'volleyball' || programId === 'court_volleyball' || programId === 'court_sports') {
    templateId = 'volleyball';
    programType = 'volleyball';
    if (month >= 7 && month <= 10) {
      season = 'fall';
    } else if (month >= 11 || month <= 4) {
      season = 'club';
    } else {
      season = 'summer';
    }
  }

  // Functional Beginner — non-seasonal, starts from Q1 on day one.
  if (programId === 'functional_beginner' || programId === 'functional_strength_beginner') {
    templateId = 'functional-beginner-template';
    programType = 'functional_beginner';
  }

  // Multi-Sport Sprint — same pattern as Volleyball: full program in programs/multi_sport_sprint.
  if (programId === 'multi_sport_sprint' || programId === 'multi_sport_training') {
    templateId = 'multi_sport_sprint';
    if (month >= 11 || month <= 1) {
      programType = 'multi_sport_winter';
      season = 'winter';
    } else if (month >= 2 && month <= 4) {
      programType = 'multi_sport_spring';
      season = 'spring';
    } else if (month >= 5 && month <= 7) {
      programType = 'multi_sport_summer';
      season = 'summer';
    } else {
      programType = 'multi_sport_fall';
      season = 'fall';
    }
  }

  console.log(`[setupProgramByPersona] Resolved template: ${templateId}, season: ${season}`);

  // Fetch template from Firestore
  const template = await fetchProgramTemplate(templateId);
  if (!template) {
    console.error(`[setupProgramByPersona] No Firestore template found for ${templateId}`);
    return { success: false, programType, message: `Program template "${templateId}" not found in database` };
  }

  // If the template declares programScheduleType, let it override the hardcoded season logic.
  // "day1" programs always start fresh — no season, programType stays as-is.
  if (template.programScheduleType === 'day1') {
    season = null;
  }

  // Ensure the template has a programStartDate (default to today)
  if (!template.programStartDate) {
    template.programStartDate = formatDateStr(new Date());
  }
  // Ensure it has a programId
  if (!template.programId) {
    template.programId = templateId;
  }

  try {
    const result = await setupProgramForUser(userId, template);
    return { ...result, programType, season };
  } catch (error) {
    console.error(`[setupProgramByPersona] Error setting up program:`, error);
    throw error;
  }
}

/**
 * Shift all activities by N days
 */
export async function shiftAllActivities(userId, days) {
  try {
    const activitiesRef = collection(db, 'users', userId, 'scheduledActivities');
    const snapshot = await getDocs(query(activitiesRef));
    
    const batch = writeBatch(db);
    let count = 0;

    snapshot.docs.forEach(doc => {
      const data = doc.data();
      const currentDate = new Date(data.scheduledDate + 'T00:00:00');
      currentDate.setDate(currentDate.getDate() + days);
      
      const newDateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
      
      batch.update(doc.ref, {
        scheduledDate: newDateStr,
        updatedAt: serverTimestamp(),
      });
      count++;
    });

    await batch.commit();
    return count;
  } catch (error) {
    console.error('[shiftAllActivities] Error:', error);
    throw error;
  }
}

/**
 * Clear all scheduled activities
 */
export async function clearAllScheduledActivities(userId) {
  try {
    const activitiesRef = collection(db, 'users', userId, 'scheduledActivities');
    const snapshot = await getDocs(query(activitiesRef));
    
    const batch = writeBatch(db);
    let count = 0;

    snapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
      count++;
    });

    await batch.commit();
    return count;
  } catch (error) {
    console.error('[clearAllScheduledActivities] Error:', error);
    throw error;
  }
}

// ============================================
// PROGRAM UPDATE/RESET FUNCTIONS
// ============================================

/**
 * Current template version - increment this when templates are updated
 * Changelog:
 * - 2026-01-07-v4: Force update for users who already updated to v3
 * - 2026-01-07-v3: MAJOR FIX - Multi-Sport templates now properly link to workout templates
 *                  Added weekly rotation (Week 1: A/B/C, Week 2: D/E/F)
 *                  Each macrocycle has distinct templates (M1, M2, M3, M4)
 * - 2026-01-07-v2: Added VB Club Day C (Explosion & Agility), fixed duplicate exercises,
 *                  added finisher rotation, removed leg_swings, new agility exercises
 */
export const TEMPLATE_VERSION = '2026-01-07-v4';

/**
 * Programs that have updated templates
 */
export const UPDATABLE_PROGRAMS = [
  'vb_club_2026',
  'vb_school_2026',
  'multi_sport_sprint',
  'court_volleyball',
  'court_sports',
  'multi_sport_training',
];

/**
 * Check if user needs a program update
 * @param {string} userId - User ID
 * @returns {Promise<{needsUpdate: boolean, programId: string, programName: string}>}
 */
export async function checkProgramUpdateAvailable(userId) {
  try {
    const userProfile = await getUserProfile(userId);
    if (!userProfile?.currentProgramId) {
      return { needsUpdate: false, programId: null, programName: null };
    }

    const programId = userProfile.currentProgramId;
    const userTemplateVersion = userProfile.templateVersion;
    
    // Check if this is an updatable program
    const isUpdatableProgram = UPDATABLE_PROGRAMS.some(p => 
      programId === p || programId.includes('vb_') || programId.includes('multi_sport')
    );
    
    if (!isUpdatableProgram) {
      return { needsUpdate: false, programId, programName: null };
    }
    
    // Fetch published templateVersion from Firestore (programs or programTemplates)
    const [programSnap, templateSnap] = await Promise.all([
      getDoc(doc(db, 'programs', programId)),
      getDoc(doc(db, 'programTemplates', programId)),
    ]);
    const publishedVersion = programSnap.exists() && programSnap.data().templateVersion
      ? programSnap.data().templateVersion
      : (templateSnap.exists() && templateSnap.data().templateVersion
        ? templateSnap.data().templateVersion
        : TEMPLATE_VERSION); // fallback to constant for programs not yet published via admin
    
    // Check if user has already applied this version
    if (userTemplateVersion === publishedVersion) {
      return { needsUpdate: false, programId, programName: null };
    }
    
    // Get program name
    const userProgramRef = doc(db, 'users', userId, 'programs', programId);
    const userProgramSnap = await getDoc(userProgramRef);
    const programName = userProgramSnap.exists() ? userProgramSnap.data().name : programId;
    
    return { 
      needsUpdate: true, 
      programId,
      programName,
      currentVersion: userTemplateVersion || 'none',
      newVersion: publishedVersion,
    };
  } catch (error) {
    console.error('[checkProgramUpdateAvailable] Error:', error);
    return { needsUpdate: false, programId: null, programName: null };
  }
}

/**
 * Reset and regenerate program workouts while preserving progress
 * - Deletes ONLY future scheduled activities
 * - Keeps completed activities and activity logs
 * - Regenerates future workouts from updated templates
 * - Maintains current mesocycle/week position
 * 
 * @param {string} userId - User ID
 * @param {string} [templateVersion] - Version to set on user profile after reset. Defaults to TEMPLATE_VERSION.
 * @returns {Promise<{success: boolean, deletedCount: number, createdCount: number, newVersion: string}>}
 */
export async function resetProgramWorkouts(userId, templateVersion) {
  try {
    console.log(`[resetProgramWorkouts] Starting reset for user: ${userId}`);
    
    const userProfile = await getUserProfile(userId);
    if (!userProfile?.currentProgramId) {
      throw new Error('No active program found');
    }
    
    const programId = userProfile.currentProgramId;
    const today = new Date();
    const todayStr = formatDateStr(today);
    
    // Step 1: Delete ONLY future scheduled activities (preserve completed/past ones)
    const activitiesRef = collection(db, 'users', userId, 'scheduledActivities');
    const futureQuery = query(
      activitiesRef,
      where('scheduledDate', '>=', todayStr),
      where('status', '==', 'scheduled')
    );
    
    const futureSnapshot = await getDocs(futureQuery);
    const batch = writeBatch(db);
    let deletedCount = 0;
    
    futureSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
      deletedCount++;
    });
    
    console.log(`[resetProgramWorkouts] Deleting ${deletedCount} future activities`);
    
    // Step 2: Regenerate future activities based on program type
    let createdCount = 0;
    
    // Calculate weeks remaining (default 8 weeks for VB, 12 for multi-sport)
    const weeksRemaining = programId.includes('vb_') ? 8 : 12;
    
    // Get program details for regeneration (verify program exists)
    const programRef = doc(db, 'users', userId, 'programs', programId);
    const programSnap = await getDoc(programRef);
    if (!programSnap.exists()) {
      console.warn('[resetProgramWorkouts] Program not found:', programId);
    }
    
    // Fetch program template from Firestore for DB-driven regeneration
    const firestoreTemplate = await fetchProgramTemplate(programId);

    if (firestoreTemplate && hasMacrocycleActivityMix(firestoreTemplate)) {
      console.log('[resetProgramWorkouts] Regenerating from weekly_activity_mix');
      const macros = firestoreTemplate.macrocycles || [];
      const mesos = firestoreTemplate.mesocycles || collectMesocyclesFromMacros(macros);
      const strengthRotation = {};

      // Recompute macrocycle/mesocycle dates and patch Firestore docs
      const pYear = inferProgramYear(macros, today);
      let macroRunning = new Date(today);
      for (const m of macros) {
        const monthRange = macroMonthRange(m, pYear);
        if (monthRange) {
          m.startDate = monthRange.startDate;
          m.endDate = monthRange.endDate;
        } else {
          const weeks = m.duration_weeks || m.durationWeeks || 12;
          if (!m.startDate) m.startDate = formatDateStr(macroRunning);
          if (!m.endDate) {
            const end = new Date(macroRunning);
            end.setDate(end.getDate() + (weeks * 7) - 1);
            m.endDate = formatDateStr(end);
          }
        }
        const macroId = m.macrocycleId || m.macrocycle_id || m.id;

        // Patch the user's macrocycle doc with correct dates and field names
        if (macroId) {
          const smVal = parseInt(m.start_month ?? m.startMonth, 10) || null;
          const emVal = parseInt(m.end_month ?? m.endMonth, 10) || null;
          const macroDocRef = doc(db, 'users', userId, 'macrocycles', macroId);
          batch.set(macroDocRef, {
            id: macroId,
            name: m.name || null,
            order: m.order ?? null,
            startDate: m.startDate,
            endDate: m.endDate,
            start_month: smVal,
            end_month: emVal,
            startMonth: smVal,
            endMonth: emVal,
            phase: m.phase || null,
            updatedAt: serverTimestamp(),
          }, { merge: true });
        }

        let mesoRunning = new Date(m.startDate + 'T00:00:00');
        const linkedMesos = mesos.filter((ms) => (ms.macrocycle_id || ms.macrocycleId) === macroId);
        for (const ms of linkedMesos) {
          const mw = ms.totalWeeks || ms.duration_weeks || ms.durationWeeks || 4;
          if (!ms.startDate) ms.startDate = formatDateStr(mesoRunning);
          if (!ms.endDate) {
            const end = new Date(mesoRunning);
            end.setDate(end.getDate() + (mw * 7) - 1);
            ms.endDate = formatDateStr(end);
          }
          mesoRunning.setDate(mesoRunning.getDate() + (mw * 7));
        }
        macroRunning = new Date(m.endDate + 'T00:00:00');
        macroRunning.setDate(macroRunning.getDate() + 1);
      }

      console.log(`[resetProgramWorkouts] Patched ${macros.length} macrocycle docs`);

      for (let week = 0; week < weeksRemaining; week++) {
        for (let day = 0; day < 7; day++) {
          const date = new Date(today);
          date.setDate(today.getDate() + (week * 7) + day);
          if (date < today) continue;

          const dayName = getDayName(date);
          const macro = findActiveMacrocycle(macros, date);
          if (!macro) continue;
          const mix = macro.weekly_activity_mix || firestoreTemplate.weekly_activity_mix;
          if (!mix?.day_assignments) {
            throw new Error(`[resetProgramWorkouts] No weekly_activity_mix.day_assignments for macrocycle "${macro.name || macro.macrocycle_id}".`);
          }
          const dayAssignments = mix.day_assignments;

          const raw = dayAssignments[dayName];
          const acts = Array.isArray(raw) ? raw : (raw ? [raw] : []);

          for (const actType of acts) {
            const lower = (actType || '').toLowerCase();
            if (lower === 'rest' || lower === 'recovery') continue;

            const mapped = mapAdminActivityType(lower);
            let workoutTemplateId = null;

            if (mapped.activityType === 'strength_workout') {
              const meso = findActiveMesocycle(mesos, macro, date);
              const pool = meso?.workout_templates || [];
              if (pool.length > 0) {
                const mesoKey = meso?.id || meso?.mesocycle_id || 'default';
                if (!strengthRotation[mesoKey]) strengthRotation[mesoKey] = 0;
                workoutTemplateId = pool[strengthRotation[mesoKey] % pool.length];
                strengthRotation[mesoKey]++;
              }
            }

            const activityRef = doc(collection(db, 'users', userId, 'scheduledActivities'));
            batch.set(activityRef, {
              scheduledDate: formatDateStr(date),
              scheduledTime: null,
              activityType: mapped.activityType,
              source: 'program',
              programId,
              workoutTemplateId,
              name: mapped.name,
              description: null,
              estimatedDuration: mapped.duration,
              duration: mapped.duration,
              status: 'scheduled',
              weekNumber: week + 1,
              isEdited: false,
              completedAt: null,
              completionSource: null,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            });
            createdCount++;
          }
        }
      }

    } else if (firestoreTemplate?.generatedSchedule && Object.keys(firestoreTemplate.generatedSchedule).length > 0) {
      // Use pre-built generatedSchedule
      console.log('[resetProgramWorkouts] Regenerating from generatedSchedule');
      for (const [scheduledDate, dayActivities] of Object.entries(firestoreTemplate.generatedSchedule)) {
        if (scheduledDate < todayStr || !Array.isArray(dayActivities)) continue;
        for (const activity of dayActivities) {
          const activityRef = doc(collection(db, 'users', userId, 'scheduledActivities'));
          batch.set(activityRef, {
            scheduledDate,
            scheduledTime: null,
            activityType: activity.activityType || 'strength_workout',
            source: 'program',
            programId,
            workoutTemplateId: activity.workoutTemplateId || null,
            name: activity.name || null,
            description: activity.description || null,
            estimatedDuration: activity.estimatedDuration ?? 60,
            duration: activity.estimatedDuration ?? 60,
            status: 'scheduled',
            weekNumber: activity.weekNumber ?? null,
            isEdited: false,
            completedAt: null,
            completionSource: null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          createdCount++;
        }
      }

    } else if (firestoreTemplate?.weeklyTemplates && firestoreTemplate?.seasonalRules) {
      // Legacy: seasonal rules + weekly templates (multi-sport triathlon)
      console.log('[resetProgramWorkouts] Regenerating from weeklyTemplates + seasonalRules');
      for (let week = 0; week < weeksRemaining; week++) {
        for (let day = 0; day < 7; day++) {
          const date = new Date(today);
          date.setDate(today.getDate() + (week * 7) + day);
          if (date < today) continue;

          const dayName = getDayName(date);
          const weeklyTemplate = getWeeklyTemplateForDate(date, firestoreTemplate.seasonalRules, firestoreTemplate.weeklyTemplates);
          if (!weeklyTemplate?.[dayName]) continue;

          for (const activity of weeklyTemplate[dayName]) {
            const activityRef = doc(collection(db, 'users', userId, 'scheduledActivities'));
            batch.set(activityRef, {
              scheduledDate: formatDateStr(date),
              scheduledTime: null,
              activityType: activity.activityType || 'endurance',
              source: 'program',
              programId,
              workoutTemplateId: activity.workoutTemplateId || null,
              name: activity.name,
              description: activity.description || null,
              estimatedDuration: activity.estimatedDuration || 60,
              duration: activity.estimatedDuration || 60,
              status: 'scheduled',
              weekNumber: week + 1,
              isEdited: false,
              completedAt: null,
              completionSource: null,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            });
            createdCount++;
          }
        }
      }
    } else {
      console.warn('[resetProgramWorkouts] No template data found for:', programId);
    }
    
    // Step 3: Update user profile with new template version
    const versionToSet = templateVersion || TEMPLATE_VERSION;
    const userRef = doc(db, 'users', userId);
    batch.update(userRef, {
      templateVersion: versionToSet,
      lastProgramReset: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    await batch.commit();
    
    console.log(`[resetProgramWorkouts] Complete! Deleted: ${deletedCount}, Created: ${createdCount}`);
    
    return { 
      success: true, 
      deletedCount, 
      createdCount,
      newVersion: versionToSet,
    };
  } catch (error) {
    console.error('[resetProgramWorkouts] Error:', error);
    throw error;
  }
}

// ============================================
// METRICS FUNCTIONS
// ============================================

/**
 * Default pace settings (used when user hasn't set custom paces)
 */
const DEFAULT_PACE_SETTINGS = {
  runPaceSecondsPerMile: 510,      // 8:30/mile (~7 mph)
  bikingAvgMph: 14,                // 14 mph
  walkPaceMinutesPerMile: 17,      // 17:00/mile (~3.5 mph)
  swimPacePer100: 2.5,             // 2:30/100yd
};

/**
 * Convert distance to miles (for display in imperial) or km (for metric)
 * 
 * @param {number} distance - Distance value
 * @param {string} fromUnit - Source unit ('miles', 'km', 'yards', 'meters')
 * @param {string} toUnit - Target unit ('miles' or 'km')
 * @returns {number} Converted distance
 */
function convertDistance(distance, fromUnit, toUnit = 'miles') {
  if (!distance || !fromUnit) return 0;
  
  const fromLower = (fromUnit || '').toLowerCase();
  const toLower = (toUnit || '').toLowerCase();
  
  // First convert to meters (common base)
  let meters = 0;
  if (fromLower === 'miles') {
    meters = distance * 1609.34;
  } else if (fromLower === 'km') {
    meters = distance * 1000;
  } else if (fromLower === 'yards') {
    meters = distance * 0.9144;
  } else if (fromLower === 'meters' || fromLower === 'm') {
    meters = distance;
  } else {
    // Unknown unit, assume miles
    meters = distance * 1609.34;
  }
  
  // Convert from meters to target unit
  if (toLower === 'miles') {
    return meters / 1609.34;
  } else if (toLower === 'km') {
    return meters / 1000;
  }
  
  return meters / 1609.34; // Default to miles
}

/**
 * Get distance from activity - uses stored value if available, otherwise estimates
 * 
 * @param {Object} activity - Activity object with distance, distanceUnit, name, duration
 * @param {Object} paceSettings - User's pace settings (optional)
 * @param {string} displayUnit - Target display unit ('miles' or 'km')
 * @returns {number} Distance in display unit
 */
function getActivityDistance(activity, paceSettings = null, displayUnit = 'miles') {
  // If activity has stored distance, convert and return it
  if (activity.distance && activity.distanceUnit) {
    return convertDistance(activity.distance, activity.distanceUnit, displayUnit);
  }
  
  // Otherwise, estimate from activity type and duration
  return estimateDistanceFromActivity(activity.name, activity.duration || 30, paceSettings, displayUnit);
}

/**
 * Helper: Estimate distance based on activity name, duration, and user's pace settings
 * Used when Apple Health data is not available and no stored distance
 * 
 * @param {string} activityName - Name of the activity
 * @param {number} durationMinutes - Duration in minutes
 * @param {Object} paceSettings - User's pace settings (optional)
 * @param {string} displayUnit - Target display unit ('miles' or 'km')
 */
function estimateDistanceFromActivity(activityName, durationMinutes, paceSettings = null, displayUnit = 'miles') {
  const nameLower = (activityName || '').toLowerCase();
  const settings = paceSettings || DEFAULT_PACE_SETTINGS;
  
  let distanceMiles = 0;
  
  // Running activities - use user's run pace (seconds per mile)
  if (nameLower.includes('run') || nameLower.includes('trail') || nameLower.includes('track workout')) {
    const secondsPerMile = settings.runPaceSecondsPerMile || DEFAULT_PACE_SETTINGS.runPaceSecondsPerMile;
    const milesPerMinute = 60 / secondsPerMile;
    distanceMiles = durationMinutes * milesPerMinute;
  }
  // Biking activities - use user's average MPH
  else if (nameLower.includes('bike') || nameLower.includes('cycling')) {
    const mph = settings.bikingAvgMph || DEFAULT_PACE_SETTINGS.bikingAvgMph;
    const milesPerMinute = mph / 60;
    distanceMiles = durationMinutes * milesPerMinute;
  }
  // Swimming activities - estimate based on pace per 100
  else if (nameLower.includes('swim')) {
    const pacePer100 = settings.swimPacePer100 || DEFAULT_PACE_SETTINGS.swimPacePer100;
    const milesPerMinute = 0.0568 / pacePer100;
    distanceMiles = durationMinutes * milesPerMinute;
  }
  // Walking - use user's walk pace (minutes per mile)
  else if (nameLower.includes('walk')) {
    const minutesPerMile = settings.walkPaceMinutesPerMile || DEFAULT_PACE_SETTINGS.walkPaceMinutesPerMile;
    const milesPerMinute = 1 / minutesPerMile;
    distanceMiles = durationMinutes * milesPerMinute;
  }
  // Cross country skiing (~6 mph = 0.1 miles per minute)
  else if (nameLower.includes('ski')) {
    distanceMiles = durationMinutes * 0.1;
  }
  // Strength training and other activities don't contribute to distance
  
  // Convert to display unit if needed
  if (displayUnit === 'km') {
    return distanceMiles * 1.60934;
  }
  return distanceMiles;
}

/**
 * Helper: Format daily metrics into 12-point chart data with date labels
 */
function formatMetricsForChart(dailyMetrics, dataSource) {
  const sortedDates = Object.keys(dailyMetrics).sort();
  const durationValues = [];
  const distanceValues = [];
  
  // Helper to format date as "M/D" (e.g., "1/7" for Jan 7)
  const formatDateLabel = (dateStr) => {
    const [, month, day] = dateStr.split('-');
    return `${parseInt(month, 10)}/${parseInt(day, 10)}`;
  };
  
  if (sortedDates.length > 0) {
    // Group days into 12 periods
    const daysPerPeriod = Math.ceil(sortedDates.length / 12);
    
    for (let i = 0; i < 12; i++) {
      const startIdx = i * daysPerPeriod;
      const endIdx = Math.min(startIdx + daysPerPeriod, sortedDates.length);
      
      let periodDuration = 0;
      let periodDistance = 0;
      let dateLabel = '';
      
      for (let j = startIdx; j < endIdx; j++) {
        const date = sortedDates[j];
        periodDuration += dailyMetrics[date].duration || 0;
        periodDistance += dailyMetrics[date].distance || 0;
        
        // Use the first date's label, or range if multiple days
        if (j === startIdx) {
          dateLabel = formatDateLabel(date);
        }
        if (j === endIdx - 1 && endIdx - startIdx > 1) {
          // Multiple days in period - show range
          dateLabel = `${dateLabel} - ${formatDateLabel(date)}`;
        }
      }
      
      durationValues.push({ value: Math.round(periodDuration), date: dateLabel });
      distanceValues.push({ value: periodDistance, date: dateLabel }); // Keep decimal for distance
    }
  } else {
    // No data - fill with zeros and generate date labels for current month
    const now = new Date();
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const daysPerPeriod = Math.ceil(daysInMonth / 12);
    
    for (let i = 0; i < 12; i++) {
      const startDay = i * daysPerPeriod + 1;
      const endDay = Math.min(startDay + daysPerPeriod - 1, daysInMonth);
      const monthNum = now.getMonth() + 1;
      const dateLabel = startDay === endDay 
        ? `${monthNum}/${startDay}` 
        : `${monthNum}/${startDay} - ${monthNum}/${endDay}`;
      
      durationValues.push({ value: 0, date: dateLabel });
      distanceValues.push({ value: 0, date: dateLabel });
    }
  }
  
  console.log(`[formatMetricsForChart] Data source: ${dataSource}, Duration values:`, durationValues);
  console.log(`[formatMetricsForChart] Distance values:`, distanceValues);
  
  return {
    duration: durationValues,
    distance: distanceValues,
    dataSource: dataSource, // 'apple_health' | 'activity_log' | 'estimates'
  };
}

/**
 * Get activity metrics (duration and distance) from completed activities for a date range
 * Priority: Apple Health data > activityLog metrics > estimates from scheduledActivities
 * 
 * @param {string} userId - User ID
 * @param {number} year - Year (e.g., 2026) - used if startDateStr/endDateStr not provided
 * @param {number} month - Month (0-11, where 0 = January) - used if startDateStr/endDateStr not provided
 * @param {string} startDateStr - Optional start date in YYYY-MM-DD format
 * @param {string} endDateStr - Optional end date in YYYY-MM-DD format
 * @returns {Promise<{duration: Array<{value: number}>, distance: Array<{value: number}>, dataSource: string}>}
 */
export async function getMonthlyActivityMetrics(userId, year, month, startDateStr = null, endDateStr = null) {
  // Use provided date range or fall back to calendar month
  const startDate = startDateStr || `${year}-${String(month + 1).padStart(2, '0')}-01`;
  const endDate = endDateStr || `${year}-${String(month + 1).padStart(2, '0')}-31`;
  
  console.log(`[getMonthlyActivityMetrics] Querying: userId=${userId}, year=${year}, month=${month}, startDate=${startDate}, endDate=${endDate}`);
  
  // Check if user has Apple Health connected and get pace settings
  let appleHealthConnected = false;
  let paceSettings = null;
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      appleHealthConnected = userData.appleHealthConnected || false;
      paceSettings = userData.paceSettings || null;
      
      if (paceSettings) {
        console.log('[getMonthlyActivityMetrics] Using user pace settings:', paceSettings);
      }
    }
  } catch (err) {
    console.log('[getMonthlyActivityMetrics] Could not check user settings:', err);
  }
  
  // PRIORITY 1: If Apple Health is connected, try to get data from appleHealth collection
  if (appleHealthConnected) {
    try {
      const appleHealthRef = collection(db, 'users', userId, 'appleHealth');
      const ahQuery = query(
        appleHealthRef,
    where('date', '>=', startDate),
    where('date', '<=', endDate),
        orderBy('date', 'asc')
      );
      
      const ahSnapshot = await getDocs(ahQuery);
      if (ahSnapshot.docs.length > 0) {
        console.log(`[getMonthlyActivityMetrics] Using Apple Health data (${ahSnapshot.docs.length} days)`);
        
        const dailyMetrics = {};
        ahSnapshot.docs.forEach(doc => {
          const data = doc.data();
          dailyMetrics[data.date] = {
            duration: data.totalDuration || 0, // Total workout duration from Apple Health
            distance: data.totalDistance || 0, // Total distance from Apple Health
          };
        });
        
        return formatMetricsForChart(dailyMetrics, 'apple_health');
      }
    } catch (err) {
      console.log('[getMonthlyActivityMetrics] Apple Health query failed, falling back to estimates:', err);
    }
  }
  
  // PRIORITY 2: Check activityLog for metrics (includes manual entries with duration)
  try {
    const activityLogRef = collection(db, 'users', userId, 'activityLog');
    const logQuery = query(
      activityLogRef,
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      where('hasCompletedActivity', '==', true),
      orderBy('date', 'asc')
    );
    
    const logSnapshot = await getDocs(logQuery);
    if (logSnapshot.docs.length > 0) {
      console.log(`[getMonthlyActivityMetrics] Using activityLog data (${logSnapshot.docs.length} days)`);
      
      const dailyMetrics = {};
      logSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const metrics = data.metrics || {};
        const completedActivities = data.completedActivities || [];
        
        console.log(`[getMonthlyActivityMetrics] Date: ${data.date}, completedActivities:`, completedActivities, 'metrics:', metrics);
        
        // Sum up duration - prefer seconds precision, fall back to minutes
        let totalDurationSeconds = metrics.workoutDurationSeconds || 0;
        if (totalDurationSeconds === 0) {
          // Try to sum from completedActivities with seconds precision
          if (completedActivities.length > 0) {
            totalDurationSeconds = completedActivities.reduce((sum, a) => {
              // Prefer durationSeconds, fall back to duration * 60
              return sum + (a.durationSeconds || (a.duration || 0) * 60);
            }, 0);
          }
          // If still 0, use workoutDuration (in minutes) * 60
          if (totalDurationSeconds === 0 && metrics.workoutDuration) {
            totalDurationSeconds = metrics.workoutDuration * 60;
          }
        }
        // Convert to minutes for display (with decimal precision)
        const totalDuration = totalDurationSeconds / 60;
        
        // Calculate distance from activities - use stored distance when available
        let totalDistance = 0;
        if (completedActivities.length > 0) {
          completedActivities.forEach(activity => {
            // Use stored distance if available, otherwise estimate
            const activityDistance = getActivityDistance(activity, paceSettings, 'miles');
            console.log(`[getMonthlyActivityMetrics] Activity: "${activity.name}", Stored distance: ${activity.distance || 'N/A'} ${activity.distanceUnit || ''}, Calculated: ${activityDistance.toFixed(2)}mi`);
            totalDistance += activityDistance;
          });
        }
        
        dailyMetrics[data.date] = {
          duration: totalDuration,
          distance: totalDistance,
        };
      });
      
      if (Object.keys(dailyMetrics).length > 0) {
        return formatMetricsForChart(dailyMetrics, 'activity_log');
      }
    }
  } catch (err) {
    console.log('[getMonthlyActivityMetrics] activityLog query failed, falling back to scheduled activities:', err);
  }
  
  // PRIORITY 3: Fall back to scheduledActivities with estimates
  const activitiesRef = collection(db, 'users', userId, 'scheduledActivities');
  const q = query(
    activitiesRef,
    where('scheduledDate', '>=', startDate),
    where('scheduledDate', '<=', endDate),
    where('status', '==', 'completed'),
    orderBy('scheduledDate', 'asc')
  );
  
  try {
  const snapshot = await getDocs(q);
    const activities = snapshot.docs.map(doc => ({
      activityId: doc.id,
      ...doc.data()
    }));
    
    console.log(`[getMonthlyActivityMetrics] Using scheduled activities with estimates (${activities.length} completed)`);
    
    // Helper function to get default duration based on activity type
    const getDefaultDuration = (activityName) => {
      const nameLower = (activityName || '').toLowerCase();
      if (nameLower.includes('strength') || nameLower.includes('workout')) return 45;
      if (nameLower.includes('run') || nameLower.includes('trail')) return 30;
      if (nameLower.includes('bike') || nameLower.includes('cycling')) return 60;
      if (nameLower.includes('long bike')) return 90;
      if (nameLower.includes('swim')) return 45;
      if (nameLower.includes('walk')) return 30;
      return 30; // Default 30 minutes
    };
    
    // Group activities by day
    const dailyMetrics = {};
    
    activities.forEach(activity => {
      const date = activity.scheduledDate;
      if (!dailyMetrics[date]) {
        dailyMetrics[date] = {
          duration: 0,
          distance: 0
        };
      }
      
      // Get duration - prefer seconds precision, fall back to minutes
      let durationSeconds = activity.estimatedDurationSeconds;
      if (!durationSeconds) {
        // Fall back to minutes * 60
        const durationMinutes = activity.estimatedDuration || activity.duration || activity.estimated_duration_minutes;
        durationSeconds = durationMinutes ? durationMinutes * 60 : getDefaultDuration(activity.name) * 60;
      }
      const duration = durationSeconds / 60; // Convert to minutes for display
      
      console.log(`[getMonthlyActivityMetrics] Activity: ${activity.name}, Date: ${date}, Duration: ${duration}min (${durationSeconds}s), Status: ${activity.status}`);
      
      dailyMetrics[date].duration += duration;
      // Use stored distance if available, otherwise estimate
      const activityForDistance = {
        name: activity.name,
        duration: duration,
        distance: activity.estimatedDistance,
        distanceUnit: activity.distanceUnit
      };
      dailyMetrics[date].distance += getActivityDistance(activityForDistance, paceSettings, 'miles');
    });
    
    console.log('[getMonthlyActivityMetrics] Daily metrics:', dailyMetrics);
    console.log('[getMonthlyActivityMetrics] Total activities found:', activities.length);
    
    return formatMetricsForChart(dailyMetrics, 'estimates');
  } catch (error) {
    console.error('[getMonthlyActivityMetrics] Error:', error);
    // Return empty arrays on error
    const emptyData = Array(12).fill({ value: 0 });
    return {
      duration: emptyData,
      distance: emptyData,
      dataSource: 'error'
    };
  }
}

/**
 * Get activity status for the last N days (for heat strip visualization)
 * @param {string} userId - User ID
 * @param {number} days - Number of days to look back (default 30)
 * @returns {Promise<Array<{date: string, dayOfWeek: number, hasActivity: boolean}>>}
 */
export async function getActivityHeatData(userId, days = 30) {
  try {
    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(startDate.getDate() - (days - 1));
    
    const formatDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const startDateStr = formatDate(startDate);
    const endDateStr = formatDate(today);
    
    // Query activity logs for the date range
    const logRef = collection(db, 'users', userId, 'activityLog');
    const q = query(
      logRef,
      where('date', '>=', startDateStr),
      where('date', '<=', endDateStr),
      where('hasCompletedActivity', '==', true)
    );
    
    const snapshot = await getDocs(q);
    
    // Build a map of date -> completed activity names
    const completedActivitiesMap = {};
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      const activities = data.completedActivities || [];
      completedActivitiesMap[data.date] = activities.map(a => a.name || a).filter(Boolean);
    });
    
    // Build array of all days with activity status
    const heatData = [];
    const currentDate = new Date(startDate);
    
    while (currentDate <= today) {
      const dateStr = formatDate(currentDate);
      const activities = completedActivitiesMap[dateStr] || [];
      heatData.push({
        date: dateStr,
        dayOfWeek: currentDate.getDay(), // 0 = Sunday
        dayOfMonth: currentDate.getDate(),
        hasActivity: activities.length > 0,
        activities: activities,
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return heatData;
  } catch (error) {
    console.error('[getActivityHeatData] Error:', error);
    return [];
  }
}

// ============================================
// BACKFILL / DATA REPAIR FUNCTIONS
// ============================================

/**
 * Estimate distance based on activity name and duration (for backfill)
 */
function backfillEstimateDistance(activityName, durationMinutes) {
  const nameLower = (activityName || '').toLowerCase();
  
  if (nameLower.includes('run') || nameLower.includes('trail') || nameLower.includes('track workout')) {
    return durationMinutes * 0.117; // ~7 mph
  }
  if (nameLower.includes('bike') || nameLower.includes('cycling')) {
    return durationMinutes * 0.233; // ~14 mph
  }
  if (nameLower.includes('swim')) {
    return durationMinutes * 0.042; // ~2.5 mph
  }
  if (nameLower.includes('walk')) {
    return durationMinutes * 0.058; // ~3.5 mph
  }
  if (nameLower.includes('ski')) {
    return durationMinutes * 0.1; // ~6 mph
  }
  return 0;
}

/**
 * Get default duration based on activity type (for backfill)
 */
function backfillGetDefaultDuration(activityName) {
  const nameLower = (activityName || '').toLowerCase();
  if (nameLower.includes('strength') || nameLower.includes('workout')) return 45;
  if (nameLower.includes('run') || nameLower.includes('trail')) return 30;
  if (nameLower.includes('bike') || nameLower.includes('cycling')) return 60;
  if (nameLower.includes('long bike')) return 90;
  if (nameLower.includes('swim')) return 45;
  if (nameLower.includes('walk')) return 30;
  return 30;
}

/**
 * Backfill activity logs from completed scheduled activities
 * This repairs missing activityLog entries that are needed for the Monthly Activity charts.
 * 
 * @param {string} userId - User ID
 * @param {number} year - Year (e.g., 2025)
 * @param {number} month - Month (1-12, where 1 = January)
 * @returns {Promise<{created: number, updated: number, errors: number}>}
 */
export async function backfillActivityLogs(userId, year, month) {
  console.log(`[backfillActivityLogs] Starting backfill for ${month}/${year}...`);
  
  const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
  const endDate = `${year}-${String(month).padStart(2, '0')}-31`;
  
  let created = 0;
  let updated = 0;
  let errors = 0;

  try {
    // Step 1: Get all completed scheduled activities for the target month
    const activitiesRef = collection(db, 'users', userId, 'scheduledActivities');
    const q = query(
      activitiesRef,
      where('scheduledDate', '>=', startDate),
      where('scheduledDate', '<=', endDate),
      where('status', '==', 'completed')
    );

    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      console.log('[backfillActivityLogs] No completed activities found for this period.');
      return { created: 0, updated: 0, errors: 0, message: 'No completed activities found for this period.' };
    }

    console.log(`[backfillActivityLogs] Found ${snapshot.size} completed activities`);

    // Step 2: Group activities by date
    const activitiesByDate = {};
    
    snapshot.docs.forEach(doc => {
      const activity = { id: doc.id, ...doc.data() };
      const date = activity.scheduledDate;
      
      if (!activitiesByDate[date]) {
        activitiesByDate[date] = [];
      }
      activitiesByDate[date].push(activity);
    });

    console.log(`[backfillActivityLogs] Activities grouped into ${Object.keys(activitiesByDate).length} unique days`);

    // Step 3: Create/update activityLog entries for each date
    const batch = writeBatch(db);
    let batchCount = 0;
    const MAX_BATCH_SIZE = 500;

    for (const [date, activities] of Object.entries(activitiesByDate)) {
      const logRef = doc(db, 'users', userId, 'activityLog', date);
      
      try {
        const logSnap = await getDoc(logRef);

        // Calculate metrics for this date
        let totalDuration = 0;
        let totalDistance = 0;
        const completedActivities = [];

        activities.forEach(activity => {
          const duration = activity.estimatedDuration || activity.duration || backfillGetDefaultDuration(activity.name);
          const distance = backfillEstimateDistance(activity.name, duration);

          totalDuration += duration;
          totalDistance += distance;

          // Ensure name is always included
          let activityName = activity.name;
          if (!activityName && activity.id) {
            // Try to extract from activityId
            const parts = activity.id.split('-');
            const lastPart = parts[parts.length - 1];
            activityName = lastPart.charAt(0).toUpperCase() + lastPart.slice(1);
          }
          if (!activityName) {
            activityName = 'Activity';
          }
          
          completedActivities.push({
            activityId: activity.id,
            activityType: activity.activityType || 'custom',
            name: activityName, // Always include name
            duration: duration,
            completedAt: activity.completedAt || Timestamp.now(),
            completionSource: activity.completionSource || 'backfill',
          });
        });

        const logData = {
          date: date,
          completedActivities: completedActivities,
          hasCompletedActivity: true,
          metrics: {
            workoutDuration: totalDuration,
            totalDistance: totalDistance,
            lbsMoved: 0,
            setsCompleted: 0,
            repsCompleted: 0,
          },
          updatedAt: serverTimestamp(),
        };

        if (logSnap.exists()) {
          // Merge with existing data
          const existingData = logSnap.data();
          const existingActivities = existingData.completedActivities || [];
          const existingMetrics = existingData.metrics || {};
          
          // Only add activities that don't already exist
          const existingIds = new Set(existingActivities.map(a => a.activityId));
          const newActivities = completedActivities.filter(a => !existingIds.has(a.activityId));
          
          // Merge activities
          const mergedActivities = [...existingActivities, ...newActivities];
          
          // ALWAYS recalculate duration - existing entries may have missing/zero durations
          // First, update existing activities with proper duration from our calculated data
          const activityDurationMap = new Map(completedActivities.map(a => [a.activityId, a.duration]));
          
          const updatedActivities = mergedActivities.map(activity => {
            // If this activity has no duration, try to get it from our calculated data
            if (!activity.duration || activity.duration === 0) {
              const calculatedDuration = activityDurationMap.get(activity.activityId);
              if (calculatedDuration) {
                return { ...activity, duration: calculatedDuration };
              }
            }
            return activity;
          });
          
          // Recalculate total duration from all activities
          const recalculatedDuration = updatedActivities.reduce((sum, a) => sum + (a.duration || 0), 0);
          
          // Check if we need to update (new activities OR duration was missing/wrong)
          const existingDuration = existingMetrics.workoutDuration || 0;
          const needsUpdate = newActivities.length > 0 || recalculatedDuration !== existingDuration || recalculatedDuration > 0;
          
          if (needsUpdate) {
            batch.update(logRef, {
              completedActivities: updatedActivities,
              hasCompletedActivity: true,
              metrics: {
                ...existingMetrics,
                workoutDuration: recalculatedDuration,
                totalDistance: totalDistance || existingMetrics.totalDistance || 0,
              },
              updatedAt: serverTimestamp(),
            });
            updated++;
            batchCount++;
            console.log(`[backfillActivityLogs] Updated ${date}: ${updatedActivities.length} activities, ${recalculatedDuration}min total (was ${existingDuration}min)`);
          }
    } else {
          // Create new log entry
          logData.createdAt = serverTimestamp();
          batch.set(logRef, logData);
          created++;
          batchCount++;
          console.log(`[backfillActivityLogs] Created ${date}: ${completedActivities.length} activities, ${totalDuration}min, ${totalDistance.toFixed(1)}mi`);
        }

        // Commit batch if approaching limit
        if (batchCount >= MAX_BATCH_SIZE) {
          await batch.commit();
          console.log(`[backfillActivityLogs] Committed batch of ${batchCount} operations`);
          batchCount = 0;
        }
      } catch (dateError) {
        console.error(`[backfillActivityLogs] Error processing ${date}:`, dateError);
        errors++;
      }
    }

    // Commit remaining batch operations
    if (batchCount > 0) {
      await batch.commit();
      console.log(`[backfillActivityLogs] Committed final batch of ${batchCount} operations`);
    }

    console.log(`[backfillActivityLogs] Complete! Created: ${created}, Updated: ${updated}, Errors: ${errors}`);
  return {
      created, 
      updated, 
      errors,
      message: `Backfill complete! Created ${created} new logs, updated ${updated} existing logs.`
    };
  } catch (error) {
    console.error('[backfillActivityLogs] Error:', error);
    return { 
      created, 
      updated, 
      errors: errors + 1,
      message: `Error during backfill: ${error.message}`
    };
  }
}

// ============================================
// WORKOUT SESSION FUNCTIONS
// ============================================

/**
 * Save a completed workout session
 * @param {string} userId - User ID
 * @param {object} sessionData - Workout session data
 * @param {object} workoutData - Exercise and set data { warmup: [], workout: [], finisher: [] }
 * @returns {object} - { success: boolean, sessionId: string }
 */
export async function saveWorkoutSession(userId, sessionData, workoutData) {
  try {
    const batch = writeBatch(db);
    
    // Generate session ID
    const sessionId = `session_${Date.now()}`;
    const sessionRef = doc(db, 'users', userId, 'workoutSessions', sessionId);
    
    // Calculate summary from workoutData
    let totalSetsPlanned = 0;
    let totalSetsCompleted = 0;
    let totalReps = 0;
    let totalLbsMoved = 0;
    let totalExercises = 0;
    let exercisesCompleted = 0;
    
    const allExercises = [
      ...workoutData.warmup.map(e => ({ ...e, section: 'warmup' })),
      ...workoutData.workout.map(e => ({ ...e, section: 'workout' })),
      ...workoutData.finisher.map(e => ({ ...e, section: 'finisher' })),
    ];
    
    allExercises.forEach(exercise => {
      totalExercises++;
      let exerciseHasCompletedSet = false;
      
      exercise.sets.forEach(set => {
        totalSetsPlanned++;
        if (set.status === 'completed') {
          totalSetsCompleted++;
          exerciseHasCompletedSet = true;
          const reps = set.actualReps || set.plannedReps || 0;
          const weight = set.actualWeight || set.plannedWeight || 0;
          totalReps += reps;
          totalLbsMoved += reps * weight;
        }
      });
      
      if (exerciseHasCompletedSet) {
        exercisesCompleted++;
      }
    });
    
    // Create session document
    const sessionDoc = {
      sessionId,
      scheduledActivityId: sessionData.scheduledActivityId || null,
      workoutTemplateId: sessionData.workoutTemplateId || null,
      programId: sessionData.programId || null,
      name: sessionData.name,
      description: sessionData.description || null,
      scheduledDate: sessionData.scheduledDate,
      status: 'completed',
      startedAt: Timestamp.fromMillis(sessionData.startTime),
      finishedAt: Timestamp.now(),
      totalPausedTime: sessionData.totalPausedTime || 0,
      actualDuration: Math.round((Date.now() - sessionData.startTime - (sessionData.totalPausedTime || 0)) / 60000),
      summary: {
        totalExercises,
        exercisesCompleted,
        totalSetsPlanned,
        totalSetsCompleted,
        totalReps,
        totalLbsMoved,
      },
      createdAt: serverTimestamp(),
    };
    
    batch.set(sessionRef, sessionDoc);
    
    // Create exercise documents with sets as subcollection
    let exerciseOrder = 0;
    for (const exercise of allExercises) {
      exerciseOrder++;
      const exerciseId = `exercise_${exerciseOrder}`;
      const exerciseRef = doc(db, 'users', userId, 'workoutSessions', sessionId, 'exercises', exerciseId);
      
      const completedSets = exercise.sets.filter(s => s.status === 'completed').length;
      const exerciseTotalReps = exercise.sets
        .filter(s => s.status === 'completed')
        .reduce((sum, s) => sum + (s.actualReps || s.plannedReps || 0), 0);
      const exerciseTotalLbs = exercise.sets
        .filter(s => s.status === 'completed')
        .reduce((sum, s) => {
          const reps = s.actualReps || s.plannedReps || 0;
          const weight = s.actualWeight || s.plannedWeight || 0;
          return sum + (reps * weight);
        }, 0);
      
      const exerciseDoc = {
        exerciseId,
        sessionId,
        name: exercise.name,
        description: exercise.description || null,
        section: exercise.section,
        order: exerciseOrder,
        status: completedSets > 0 ? 'completed' : 'pending',
        plannedSets: exercise.sets.length,
        completedSets,
        totalReps: exerciseTotalReps,
        totalLbsMoved: exerciseTotalLbs,
      };
      
      batch.set(exerciseRef, exerciseDoc);
      
      // Create set documents
      for (let i = 0; i < exercise.sets.length; i++) {
        const set = exercise.sets[i];
        const setId = `set_${i + 1}`;
        const setRef = doc(db, 'users', userId, 'workoutSessions', sessionId, 'exercises', exerciseId, 'sets', setId);
        
        const setDoc = {
          setId,
          exerciseId,
          setNumber: set.setNumber || i + 1,
          setType: 'working',
          plannedReps: set.plannedReps,
          plannedWeight: set.plannedWeight,
          actualReps: set.actualReps,
          actualWeight: set.actualWeight,
          lbsMoved: set.status === 'completed' 
            ? (set.actualReps || set.plannedReps || 0) * (set.actualWeight || set.plannedWeight || 0)
            : null,
          completedAt: set.status === 'completed' ? Timestamp.now() : null,
          status: set.status,
        };
        
        batch.set(setRef, setDoc);
      }
    }
    
    // Update scheduled activity if we have an ID
    if (sessionData.scheduledActivityId) {
      const activityRef = doc(db, 'users', userId, 'scheduledActivities', sessionData.scheduledActivityId);
      batch.update(activityRef, {
        status: 'completed',
        completedAt: serverTimestamp(),
        workoutSessionId: sessionId,
        updatedAt: serverTimestamp(),
      });
    }
    
    // Update activity log for the date
    const dateStr = sessionData.scheduledDate;
    if (dateStr) {
      const logRef = doc(db, 'users', userId, 'activityLog', dateStr);
      const logSnap = await getDoc(logRef);
      
      if (logSnap.exists()) {
        const existing = logSnap.data();
        batch.update(logRef, {
          hasCompletedActivity: true,
          'metrics.totalLbsMoved': (existing.metrics?.totalLbsMoved || 0) + totalLbsMoved,
          'metrics.totalDuration': (existing.metrics?.totalDuration || 0) + sessionDoc.actualDuration,
          updatedAt: serverTimestamp(),
        });
      } else {
        batch.set(logRef, {
          date: dateStr,
          hasCompletedActivity: true,
          completedActivities: [sessionData.name],
          metrics: {
            totalDuration: sessionDoc.actualDuration,
            totalDistance: 0,
            totalLbsMoved,
          },
          createdAt: serverTimestamp(),
        });
      }
    }
    
    // Commit all changes
    await batch.commit();
    
    console.log(`[saveWorkoutSession] Saved session ${sessionId} with ${totalExercises} exercises, ${totalSetsCompleted}/${totalSetsPlanned} sets, ${totalLbsMoved} lbs`);

    // Queue for HealthKit export so it appears in Apple Health / HealthKit Sync for manual sync.
    // (Marking complete via Dashboard checkmark already queues; Workout-page save did not – that’s why those workouts were missing.)
    try {
      const userProfile = await getUserProfile(userId);
      if (userProfile?.appleHealthConnected && dateStr) {
        const activityId = sessionData.scheduledActivityId || sessionId;
        const durationMinutes = sessionDoc.actualDuration ?? 0;
        const completedActivity = {
          activityType: 'strength_workout',
          name: sessionData.name || 'Strength Workout',
          duration: durationMinutes,
          durationSeconds: durationMinutes * 60,
          distance: null,
          distanceUnit: null,
          laps: null,
          poolLength: null,
          completedAt: Timestamp.now(),
          completionSource: 'workout_app',
        };
        await queueActivityForHealthKitExport(userId, activityId, dateStr, completedActivity);
        console.log(`[saveWorkoutSession] Queued session for HealthKit export: ${activityId}`);
      }
    } catch (hkErr) {
      console.warn('[saveWorkoutSession] Failed to queue for HealthKit export:', hkErr);
    }
    
    return { 
      success: true, 
      sessionId,
      summary: sessionDoc.summary,
    };
  } catch (error) {
    console.error('[saveWorkoutSession] Error:', error);
    throw error;
  }
}

/**
 * Get the most recent completed workout session for a scheduled activity on a given date.
 * Used when tapping a completed strength workout on Dashboard to show the finish card.
 * @param {string} userId
 * @param {string} activityId - scheduledActivities doc id
 * @param {string} dateStr - YYYY-MM-DD
 * @returns {Promise<object|null>} Session doc { sessionId, name, scheduledDate, summary, actualDuration } or null
 */
export async function getWorkoutSessionForActivity(userId, activityId, dateStr) {
  if (!userId || !activityId || !dateStr) return null;
  try {
    const sessionsRef = collection(db, 'users', userId, 'workoutSessions');
    const q = query(
      sessionsRef,
      where('scheduledActivityId', '==', activityId),
      where('scheduledDate', '==', dateStr),
      orderBy('finishedAt', 'desc'),
      limit(1)
    );
    const snap = await getDocs(q);
    const doc = snap.docs[0];
    if (!doc) return null;
    const d = doc.data();
    return {
      sessionId: doc.id,
      name: d.name,
      scheduledDate: d.scheduledDate,
      summary: d.summary || {},
      actualDuration: d.actualDuration ?? 0,
    };
  } catch (e) {
    // Composite index may be required (scheduledActivityId, scheduledDate, status, finishedAt)
    console.warn('[getWorkoutSessionForActivity]', e);
    return null;
  }
}

// ============================================
// ACCOUNT DELETION FUNCTIONS
// ============================================

/**
 * Delete all user data from Firestore
 * This function deletes all collections and subcollections for a user
 */
export async function deleteAllUserData(userId) {
  try {
    console.log(`[deleteAllUserData] Starting deletion for user: ${userId}`);
    
    // Collections to delete (top-level user subcollections)
    const collectionsToDelete = [
      'scheduledActivities',
      'activityLog',
      'workoutSessions',
      'programs',
      'macrocycles',
      'mesocycles',
      'equipment',
      'training_plans',
      'custom_workouts',
      'exercise_baselines',
      'exercise_history',
      'exercise_preferences',
      'progression_predictions',
      'workoutTemplates',
      'appleHealth',
    ];
    
    // Delete each collection
    for (const collectionName of collectionsToDelete) {
      try {
        const collectionRef = collection(db, 'users', userId, collectionName);
        const snapshot = await getDocs(collectionRef);
        
        // For workoutSessions, we need to delete nested collections (exercises/sets)
        if (collectionName === 'workoutSessions') {
          let batch = writeBatch(db);
          let batchCount = 0;
          
          for (const docSnap of snapshot.docs) {
            const sessionId = docSnap.id;
            
            // Delete nested exercises collection
            const exercisesRef = collection(db, 'users', userId, 'workoutSessions', sessionId, 'exercises');
            const exercisesSnapshot = await getDocs(exercisesRef);
            
            for (const exerciseDoc of exercisesSnapshot.docs) {
              const exerciseId = exerciseDoc.id;
              
              // Delete nested sets collection
              const setsRef = collection(db, 'users', userId, 'workoutSessions', sessionId, 'exercises', exerciseId, 'sets');
              const setsSnapshot = await getDocs(setsRef);
              
              for (const setDoc of setsSnapshot.docs) {
                if (batchCount >= 500) {
                  await batch.commit();
                  batch = writeBatch(db);
                  batchCount = 0;
                }
                batch.delete(setDoc.ref);
                batchCount++;
              }
              
              if (batchCount >= 500) {
                await batch.commit();
                batch = writeBatch(db);
                batchCount = 0;
              }
              batch.delete(exerciseDoc.ref);
              batchCount++;
            }
            
            if (batchCount >= 500) {
              await batch.commit();
              batch = writeBatch(db);
              batchCount = 0;
            }
            batch.delete(docSnap.ref);
            batchCount++;
          }
          
          if (batchCount > 0) {
            await batch.commit();
          }
        } else {
          // For other collections, delete documents in batches
          let batch = writeBatch(db);
          let batchCount = 0;
          
          for (const docSnap of snapshot.docs) {
            if (batchCount >= 500) {
              await batch.commit();
              batch = writeBatch(db);
              batchCount = 0;
            }
            batch.delete(docSnap.ref);
            batchCount++;
          }
          
          if (batchCount > 0) {
            await batch.commit();
          }
        }
        
        console.log(`[deleteAllUserData] Deleted ${snapshot.size} documents from ${collectionName}`);
      } catch (error) {
        console.error(`[deleteAllUserData] Error deleting ${collectionName}:`, error);
        // Continue with other collections even if one fails
      }
    }
    
    // Finally, delete the user profile document
    try {
      const userRef = doc(db, 'users', userId);
      await deleteDoc(userRef);
      console.log(`[deleteAllUserData] Deleted user profile for ${userId}`);
    } catch (error) {
      console.error(`[deleteAllUserData] Error deleting user profile:`, error);
      throw error;
    }
    
    console.log(`[deleteAllUserData] Successfully deleted all data for user: ${userId}`);
    return { success: true };
  } catch (error) {
    console.error('[deleteAllUserData] Error:', error);
    throw error;
  }
}

/**
 * Delete user account (Firestore data + Firebase Auth)
 * This is the main function to call for account deletion
 */
export async function deleteUserAccount(userId) {
  try {
    console.log(`[deleteUserAccount] Starting account deletion for user: ${userId}`);
    
    // 1. Delete all Firestore data
    await deleteAllUserData(userId);
    
    // 2. Note: Firebase Auth deletion must be done client-side
    // This function only handles Firestore data deletion
    // The calling code should handle auth.currentUser.delete() and signOut()
    
    console.log(`[deleteUserAccount] Successfully deleted account data for user: ${userId}`);
    return { success: true };
  } catch (error) {
    console.error('[deleteUserAccount] Error:', error);
    throw error;
  }
}

// ============================================
// APP CONFIG (Firestore with JSON fallback)
// ============================================

/**
 * Get personas and programs config from Firestore.
 * Falls back to the local personas.json if the Firestore document doesn't exist.
 * 
 * Firestore document: config/personas
 * Structure mirrors personas.json: { experienceLevels, filteringMatrix, personas }
 * 
 * @returns {Promise<object>} - The personas config object
 */
export async function getPersonasConfig() {
  const docRef = doc(db, 'config', 'personas');
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    console.log('[getPersonasConfig] Loaded from Firestore');
    return docSnap.data();
  }
  console.error('[getPersonasConfig] No config/personas document found in Firestore');
  return null;
}

/**
 * Fetch program catalog data (name, description) from Firestore.
 * Returns a map of programId → { name, displayName, description }
 * so the onboarding flow can show live program names from the DB.
 * 
 * @returns {Promise<Object.<string, { name: string, displayName: string, description: string }>>}
 */
export async function getProgramsCatalog() {
  try {
    const snap = await getDocs(collection(db, 'programs'));
    const catalog = {};
    snap.docs.forEach((d) => {
      const data = d.data();
      catalog[d.id] = {
        name: data.displayName || data.name || d.id,
        displayName: data.displayName || data.name,
        description: data.description || '',
      };
    });
    console.log(`[getProgramsCatalog] Loaded ${Object.keys(catalog).length} programs from Firestore`);
    return catalog;
  } catch (error) {
    console.error('[getProgramsCatalog] Error:', error);
    return {};
  }
}

/**
 * Seed (or update) the Firestore config/personas document from the local JSON.
 * Call once from an admin page or browser console to bootstrap the DB.
 * After seeding, all changes should be made in Firestore directly.
 * 
 * @returns {Promise<{ success: boolean }>}
 */
/**
 * Update a program entry inside config/personas when a program is published.
 * Ensures the program appears under the correct persona with correct metadata.
 */
export async function syncProgramToPersonasConfig(programDocId, { name, description, categoryId, status }) {
  const docRef = doc(db, 'config', 'personas');
  const snap = await getDoc(docRef);
  if (!snap.exists()) {
    console.error('[syncProgramToPersonasConfig] config/personas does not exist — seed first');
    return;
  }
  const config = snap.data();
  const personas = config.personas || [];
  let updated = false;

  for (const persona of personas) {
    const programs = persona.programs || [];
    const existingIdx = programs.findIndex(
      (p) => p.programId === programDocId || p.id === programDocId
    );

    if (persona.id === categoryId) {
      if (existingIdx >= 0) {
        programs[existingIdx] = {
          ...programs[existingIdx],
          name: name || programs[existingIdx].name,
          description: description || programs[existingIdx].description,
          programId: programDocId,
          status: status || programs[existingIdx].status,
        };
      } else {
        const newId = programDocId.replace(/[^a-z0-9_]/g, '_');
        programs.push({
          id: newId,
          name: name || programDocId,
          programId: programDocId,
          description: description || '',
          status: status || 'available',
        });
      }
      persona.programs = programs;
      updated = true;
    } else if (existingIdx >= 0) {
      // Program moved to a different persona — remove from old one
      programs.splice(existingIdx, 1);
      persona.programs = programs;
      updated = true;
    }
  }

  if (updated) {
    await setDoc(docRef, { ...config, personas });
    console.log(`[syncProgramToPersonasConfig] Updated config/personas for ${programDocId}`);
  }
}

export async function seedPersonasConfig() {
  try {
    const data = await import('../data/personas.json');
    const payload = data.default || data;
    const docRef = doc(db, 'config', 'personas');
    await setDoc(docRef, payload);
    console.log('[seedPersonasConfig] Successfully seeded config/personas');
    return { success: true };
  } catch (error) {
    console.error('[seedPersonasConfig] Error:', error);
    throw error;
  }
}
